import React, { useState, useRef, useCallback } from "react";
import {
  Button,
  CircularProgress,
  Fab,
} from "@mui/material";
import {
  TextField,
  Menu,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Radio, RadioGroup, FormControl } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import ChatIcon from "@mui/icons-material/Chat";
import ChatWindow from "./Helpers/ChatWindow";
import Navbar from "../../../globals/components/Navbar";
import { BondNumberSelect } from "./Helpers/BondNumberSelect";
import { useEffect } from "react";
import { apiClient } from "../../../globals/utils/api";
import { loadGoogleMaps } from "../../../globals/utils/googleMapsLoader";
import CreatableSelect from "react-select/creatable";
import { FixedSizeList as List } from "react-window";
import { useSelector } from "react-redux";
import { CheckCircle } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { InputAdornment } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import HistoryIcon from "@mui/icons-material/History";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { updateBondNumber } from "../../../globals/utils/commons";
import InputMask from "react-input-mask";
import { NumericFormat } from "react-number-format";
import { calculatePremium } from "../../../globals/utils/commons";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useDispatch } from "react-redux";
import { increment } from "../../../store/slices/countSlice";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import { ObligeeInformationForm } from "./Helpers/ObligeeInformation";
import { is_prod } from "../../../globals/utils/constants";

// Custom dropdown styling (matches MUI styles)
const customStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "transparent", // Make the background transparent
    minHeight: "56px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    boxShadow: "none",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for dropdown menu
    fontSize: "16px", // Decrease font size
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999, // Increase z-index for menu portal
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#6200ea"
      : state.isFocused
      ? "#f0f0f0"
      : "#fff",
    color: state.isSelected ? "#fff" : "#000",
    fontSize: "16px", // Decrease font size
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  }),
};

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      fixedDecimalScale
    />
  );
});

// Labeled CreatableSelect Component
const LabeledCreatableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  return (
    <Box sx={{ marginBottom: "2px" }}>
      {/* Label styled to mimic MUI's TextField label */}
      <Typography
        sx={{
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.6)",
          marginBottom: "4px",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <CreatableSelect
        isClearable
        options={options}
        components={{ MenuList }}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        styles={customStyles}
        isDisabled={disabled}
      />
    </Box>
  );
};

// Virtualized MenuList for better performance
const MenuList = (props) => {
  const height = 35;
  const { options, children, maxHeight } = props;

  return (
    <List
      height={Math.min(maxHeight, options.length * height)}
      itemCount={children.length}
      itemSize={height}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

// Employment Status <-> stored occupation helpers.
// We store both values inside the existing `occupation` field using a hyphen separator:
// - "Unemployed"
// - "Employed - Accountant"
// - "Retired - Teacher"
const splitEmploymentOccupation = (raw) => {
  const value = (raw || "").trim();
  if (!value) return { employmentStatus: "", occupationText: "" };
  const parts = value.split("-").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { employmentStatus: "", occupationText: "" };
  const recognized = new Set(["Employed", "Unemployed", "Retired"]);
  const first = parts[0];
  // Backwards compatibility: older records used to store only the occupation text.
  if (!recognized.has(first)) {
    return { employmentStatus: "Employed", occupationText: value };
  }
  return { employmentStatus: first, occupationText: parts.slice(1).join(" - ") };
};

const joinEmploymentOccupation = (employmentStatus, occupationText) => {
  const status = (employmentStatus || "").trim();
  const occ = (occupationText || "").trim();
  if (!status) return "";
  if (status === "Unemployed") return "Unemployed";
  if (!occ) return status;
  return `${status} - ${occ}`;
};

/** Human-readable labels for correction field names. */
const CORRECTION_FIELD_LABELS = {
  bond_amount: "Bond Amount", bond_number: "Bond Number", bond_sub_category: "Bond Sub Category",
  premium: "Premium", court_case_number: "Court Case Number", state: "State", county: "County", name: "Name",
  first_document_name: "First Document Name", second_document_name: "Second Document Name", third_document_name: "Third Document Name",
  requester_name: "Requester Name", requester_user_email: "Requester Email", requester_phone_number: "Requester Phone",
  is_underwriting: "Is Underwriting", managing_lawfirm: "Managing Law Firm", law_firm: "Law Firm", law_firm_location: "Law Firm Location",
  obligee: "Obligee", bond_carrier: "Bond Carrier", bond_type: "Bond Type", status: "Status", billing_method: "Billing Method",
};
const formatCorrectionFieldLabel = (field) => {
  const key = (field ?? "").toString().trim().toLowerCase().replace(/\s+/g, "_");
  if (!key) return "—";
  if (CORRECTION_FIELD_LABELS[key]) return CORRECTION_FIELD_LABELS[key];
  return key.split("_").map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "")).join(" ");
};
/** Format correction From/To for display: bond_amount uses US style with commas, no decimals. */
const formatCorrectionFromTo = (field, value) => {
  const f = (field ?? "").toString().toLowerCase().replace(/\s+/g, "_");
  if (f === "bond_amount") {
    const n = Number(value);
    return Number.isNaN(n) ? (value ?? "").toString() : n.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }
  return (value ?? "").toString();
};

// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["address_components", "geometry"],
          types: ["address"], // better for full addresses
        }
      );

      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();

        if (!place || !place.address_components) {
          console.error("No valid place returned or missing address components.");
          return;
        }

        let address = "",
          city = "",
          state = "",
          zipCode = "";

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            address = `${component.long_name} ${address}`;
          }
          if (types.includes("route")) {
            address += component.long_name;
          }

          // City fallbacks
          if (types.includes("locality")) {
            city = component.long_name;
          }
          if (!city && (types.includes("sublocality") || types.includes("sublocality_level_1"))) {
            city = component.long_name;
          }
          if (!city && types.includes("administrative_area_level_2")) {
            city = component.long_name;
          }

          if (types.includes("administrative_area_level_1")) {
            state = component.long_name; // ✅ full state name
          }

          if (types.includes("postal_code")) {
            zipCode = component.long_name;
          }
        });

        // Fallback: geocoder if city/state missing
        if ((!city || !state) && place.geometry?.location) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: place.geometry.location }, (results, status) => {
            if (status === "OK" && results[0]) {
              results[0].address_components.forEach((component) => {
                const types = component.types;
                if (!city && types.includes("locality")) {
                  city = component.long_name;
                }
                if (!state && types.includes("administrative_area_level_1")) {
                  state = component.long_name; // ✅ full name
                }
              });
              if (callback) callback(address, city, state, zipCode);
            }
          });
        } else {
          if (callback) callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);

  return (
    <TextField
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      sx={{ marginBottom: "8px" }}
    />
  );
};

const BasicInformationForm = ({
  globalData,
  data,
  updateData,
  role,
  isDisabled,
}) => {
  const [state, setState] = useState(null); // Initialize state as null
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false); // Loading state for counties dropdown

  // Handle input change and directly update the global state
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "bondAmount") {
      const updatedData = { ...data, [name]: value };
      updateData(updatedData);

      if (updatedData.bondAmount) {
        const bondAmount = parseFloat(
          updatedData.bondAmount.replace(/[^0-9]/g, "")
        );
        if (!isNaN(bondAmount)) {
          const premium = calculatePremium(bondAmount);
          updateData({
            ...updatedData,
            premiumAmount: premium,
            PrincipalNumber: "1",
          });
        }
      }
    } else {
      updateData({ ...data, [name]: value });
    }
  };

  const handleAutocompleteChange = (event, value, fieldName) => {
    updateData({ ...data, [fieldName]: value });
  };

  // Fetch state and bondType from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem("selectedState");
    const savedType = localStorage.getItem("selectedBondType");

    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setState(parsedState); // Parse JSON if stored as object
      updateData({ ...data, state: parsedState.label }); // Add state to data
    }

    if (savedType) {
      const parsedType = JSON.parse(savedType);
      updateData({ ...data, bondType: parsedType.label }); // Add bondType to data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch counties when state changes
  useEffect(() => {
    const fetchCounties = async () => {
      if (state && state.id) {
        setLoadingCounties(true); // Start loader
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state.id}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map(
            (county) => county.bond_county
          );
          setCounties(formattedCounties);
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false); // Stop loader
        }
      }
    };

    fetchCounties();
  }, [state]);

  // Format numbers as US currency
  const formatAsCurrency = (value) => {
    if (!value) return "";
    const numericValue = parseFloat(value);
    return numericValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        {" "}
        {/* Consistent spacing */}
        <Autocomplete
          options={counties}
          value={data.county || ""}
          onChange={(event, value) =>
            handleAutocompleteChange(event, value, "county")
          }
          loading={loadingCounties}
          disabled={isDisabled}
          renderInput={(params) => (
            <TextField
              disabled={isDisabled}
              {...params}
              label="County"
              fullWidth
              variant="outlined"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCounties ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          disabled={isDisabled}
          label="Full Legal Name of Ward/Beneficiary  "
          name="legalName"
          value={data.legalName || ""}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          disabled={isDisabled}
          label="Formal Name of the Trust"
          name="trustName"
          value={data.trustName || ""}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          disabled={isDisabled}
          label="Date of Birth of Ward/Beneficiary"
          name="dateOfBirth"
          value={data.dateOfBirth || ""}
          onChange={handleChange}
          type="date"
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          disabled={isDisabled}
          label="Bond Amount"
          name="bondAmount"
          value={data.bondAmount || ""}
          onChange={handleChange}
          fullWidth
          required
          InputProps={{
            inputComponent: NumberFormatCustom,
          }}
        />
        <TextField
          label="Premium Amount"
          name="premiumAmount"
          value={formatAsCurrency(data.premiumAmount)}
          fullWidth
          required
          disabled
        />
        <TextField
          disabled={isDisabled}
          label="Court File #"
          name="courtCaseNumber"
          value={data.courtCaseNumber || ""}
          onChange={handleChange}
          fullWidth
        />
        <TextField
          disabled={isDisabled}
          label="Number Of Principals"
          name="PrincipalNumber"
          value={data.PrincipalNumber || 1}
          onChange={handleChange}
          fullWidth
          required
          type="number"
          InputProps={{
            inputProps: {
              min: 1,
              max: 4,
            },
          }}
        />
        <TextField
          select
          label="Will you prefer to e-sign the Indemnity Agreement?"
          name="is_esignatures"
          value={data.is_esignatures}
          onChange={handleChange}
          disabled={isDisabled}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      </Box>
    </Box>
  );
};

const GeneralInformationForm = ({
  data,
  updateData,
  globalData,
  role,
  isDisabled,
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  const handleExplanationChange = (e) => {
    const { name, value } = e.target;
    updateData({
      ...data,
      explanations: {
        ...data.explanations,
        [name]: value || "", // Ensure empty explanation is handled
      },
    });
  };

  // Ensure `explanations` exists
  const explanations = data.explanations || {
    ongoingBusiness: "",
    // didLastWillAndTestament: "",
    indebtedGuardians: "",
    // replacingFiduciary: "",
    replacingPriorBond: "",
    // demandedByInterestedParty: "",
    // jointControlExercised: "",
    supplementalNeedsTrust: "",
    // outstandingLawsuitExplanation: "",
    // bankruptOrInsolventExplanation: "",
    isDeclined: "",
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        General Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        {/* US Uk Canada? */}
        <TextField
          disabled={isDisabled}
          select
          label="Are all Trustees, U.S, U.K or Canada citizens??"
          name="allUSUKCanada"
          value={data.allUSUKCanada || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>

        {/* Ongoing Business */}
        <TextField
          disabled={isDisabled}
          select
          label="Will any of the business of the wards be operated by the trustee?"
          name="ongoingBusiness"
          value={data.ongoingBusiness || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.ongoingBusiness === "Yes" && (
          <TextField
            disabled={isDisabled}
            label="Provide an explanation"
            name="ongoingBusiness"
            value={explanations.ongoingBusiness || ""}
            onChange={handleExplanationChange}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
          />
        )}

        {/* Last Will and Testament */}
        {/* <TextField disabled={isDisabled}
          select
          label="Did beneficiary execute a last will and testament?"
          name="didLastWillAndTestament"
          value={data.didLastWillAndTestament || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.didLastWillAndTestament}
          helperText={errors.didLastWillAndTestament}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField> */}

        {/* Indebted Guardians */}
        <TextField
          disabled={isDisabled}
          select
          label="Are any of the trustees indebted to the Estate?"
          name="indebtedGuardians"
          value={data.indebtedGuardians || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.indebtedGuardians === "Yes" && (
          <TextField
            disabled={isDisabled}
            label="Provide an explanation"
            name="indebtedGuardians"
            value={explanations.indebtedGuardians || ""}
            onChange={handleExplanationChange}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
          />
        )}

        {/* Replacing Fiduciary */}
        {/* <TextField disabled={isDisabled}
          select
          label="Does applicant replace any previous fiduciary?"
          name="replacingFiduciary"
          value={data.replacingFiduciary || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.replacingFiduciary}
          helperText={errors.replacingFiduciary}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.replacingFiduciary === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="replacingFiduciary"
            value={explanations.replacingFiduciary || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Replacing Prior Bond */}
        <TextField
          disabled={isDisabled}
          select
          label="Does this bond replace any prior bond?"
          name="replacingPriorBond"
          value={data.replacingPriorBond || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.replacingPriorBond === "Yes" && (
          <TextField
            disabled={isDisabled}
            label="Provide an explanation"
            name="replacingPriorBond"
            value={explanations.replacingPriorBond || ""}
            onChange={handleExplanationChange}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
          />
        )}

        {/* Declined By Surety */}
        {/* <TextField disabled={isDisabled}
          select
          label="Has the applicant for this bond been declined by another Surety?"
          name="isDeclined"
          value={data.isDeclined || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.isDeclined}
          helperText={errors.isDeclined}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.isDeclined === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="isDeclined"
            value={explanations.isDeclined || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Demanded by Interested Party */}
        {/* <TextField disabled={isDisabled}
          select
          label="Is this bond required on the demand of an interested party other than the Court?"
          name="demandedByInterestedParty"
          value={data.demandedByInterestedParty || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.demandedByInterestedParty}
          helperText={errors.demandedByInterestedParty}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.demandedByInterestedParty === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="demandedByInterestedParty"
            value={explanations.demandedByInterestedParty || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Outstanding Lawsuit */}
        {/* <TextField disabled={isDisabled}
          select
          label="Does the applicant have any outstanding lawsuits, judgment, or liens?"
          name="outstandingLawsuit"
          value={data.outstandingLawsuit || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.outstandingLawsuit}
          helperText={errors.outstandingLawsuit}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.outstandingLawsuit === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="outstandingLawsuitExplanation"
            value={explanations.outstandingLawsuitExplanation || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Ever Bankrupt */}
        {/* <TextField disabled={isDisabled}
          select
          label="Has the applicant ever been bankrupt or insolvent?"
          name="bankruptOrInsolvent"
          value={data.bankruptOrInsolvent || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.bankruptOrInsolvent}
          helperText={errors.bankruptOrInsolvent}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.bankruptOrInsolvent === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="bankruptOrInsolventExplanation"
            value={explanations.bankruptOrInsolventExplanation || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Will joint control be exercised? */}
        {/* <TextField disabled={isDisabled}
          select
          label="Will joint control be exercised?"
          name="jointControlExercised"
          value={data.jointControlExercised || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.jointControlExercised}
          helperText={errors.jointControlExercised}

        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.jointControlExercised === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="jointControlExercised"
            value={explanations.jointControlExercised || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Will a supplemental needs trust be created? */}
        <TextField
          disabled={isDisabled}
          select
          label="Will a supplemental needs trust be created?"
          name="supplementalNeedsTrust"
          value={data.supplementalNeedsTrust || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {/* {data.supplementalNeedsTrust === "Yes" && (
          <TextField disabled={isDisabled}
            label="Provide an explanation"
            name="supplementalNeedsTrust"
            value={explanations.supplementalNeedsTrust || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )} */}

        {/* Additional Question: Request for Authorization */}
        {globalData?.basicInformation?.bondAmount >= 250000 && (
          <>
            <Box
              sx={{
                border: "1px solid #d1d5db", // Light gray border
                borderRadius: "8px", // Rounded corners
                backgroundColor: "#f9fafb", // Light background
                padding: "16px", // Add spacing inside the box
                margin: "16px 0", // Add vertical spacing outside the box
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: "2px" }}>
                Authorization to Obtain Underwriting Information
              </Typography>
              <Typography>
                Do you fully understand and agree, by submitting this request
                for approval you are providing authorization for Probate Bond
                Pros, surety companies and/or third-party brokers to verify
                information you provided as well as permission to obtain your
                consumer information from any source which would be necessary to
                determine bonding eligibility? This includes obtaining credit
                reports at time of application, during any review or renewal, at
                the time of any potential or actual claim, and/or for any other
                legitimate purposes determined by the surety in its reasonable
                discretion. This information will be held in the strictest
                confidence. Do you agree?
              </Typography>
            </Box>

            <TextField
              disabled={isDisabled}
              select
              label="Authorization to obtain underwriting information?"
              name="requestForAuthorization"
              value={data.requestForAuthorization || "No"}
              onChange={handleChange}
              fullWidth
              required
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </>
        )}
      </Box>
    </Box>
  );
};

const GuardianForm = ({
  data = { guardians: [] },
  updateData,
  globalData,
  role,
  isDisabled,
  pii_access,
}) => {
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);
  const [loadingGuardians, setLoadingGuardians] = useState(false);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);
  const [showSSNStates, setShowSSNStates] = useState({});

  // Ref to track current data for async functions to avoid stale closures
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const toggleShowSSN = (index) => {
    setShowSSNStates((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Fetch cities and states for dropdowns
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    loadDropdownData();
  }, []);

  // Fetch Professional Guardians from API
  useEffect(() => {
    const fetchProfessionalGuardians = async () => {
      setLoadingGuardians(true);
      try {
        const response = await apiClient(
          "/get-all-professional-guardians",
          "GET"
        );
        const formattedGuardians = response.data.map((guardian) => ({
          value: guardian.pg_id,
          label: guardian.full_name,
          address:
            guardian.mailing_address === ", , ,"
              ? guardian.physical_address
              : guardian.mailing_address,
          cell: guardian.cell_phone,
        }));
        setProfessionalGuardians(formattedGuardians);
      } catch (error) {
        console.error("Error fetching professional guardians:", error);
      } finally {
        setLoadingGuardians(false);
      }
    };

    fetchProfessionalGuardians();
  }, []);

  // Fetch Attorneys from API
  useEffect(() => {
    const fetchAttorneys = async () => {
      setLoadingAttorneys(true);
      let id = 0;
      try {
        const response = await apiClient(`/lawfirm-attorneys/${id}`, "GET");
        const formattedAttorneys = response.map((attorney) => ({
          value: attorney.lawfirm_user_id,
          label: attorney.full_name,
          position: attorney.position,
          address: attorney.mailing_address,
          city: attorney.city,
          state: attorney.state,
          zip_code: attorney.zip_code,
        }));
        setAttorneys(formattedAttorneys);
      } catch (error) {
        console.error("Error fetching attorneys:", error);
      } finally {
        setLoadingAttorneys(false);
      }
    };

    fetchAttorneys();
  }, []);

  //Fetch details for one professional guardians
  const fetchProfessionalGuardianDetails = async (index, id) => {
    try {
      const response = await apiClient(`/guardian-details/${id}/`, "GET");
      // Use dataRef.current to get fresh state after async call
      const currentData = dataRef.current;
      const updatedGuardians = Array.isArray(currentData.guardians)
        ? [...currentData.guardians]
        : [];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        name: response.full_name,
        address: response.mailing_address,
        city: response.city,
        state: response.state,
        zip_code: response.zip_code,
        professional_guardian: id,
        is_professional_guardian: "Yes",
        is_new_pg: false,
        showFullForm: false,
      };
      updateData({ ...currentData, guardians: updatedGuardians });
    } catch (error) {
      console.error("Error fetching professional guardian details", error);
    }
  };

  // Handle attorney home address change
  const handleAttorneyHomeAddressChange = (index, value) => {
    const updatedGuardians = [...data.guardians];
    const guardian = updatedGuardians[index];
    const attorney = attorneys.find((a) => a.value === guardian.attorney);

    updatedGuardians[index] = {
      ...guardian,
      is_attorney_home: value,
      // If switching to office address, restore attorney's office address
      address: value === "No" ? attorney?.address : guardian.address,
      city: value === "No" ? attorney?.city : guardian.city,
      state: value === "No" ? attorney?.state : guardian.state,
      zip_code: value === "No" ? attorney?.zip_code : guardian.zip_code,
    };
    updateData({ ...data, guardians: updatedGuardians });
  };

  // Handle attorney selection
  const handleAttorneySelection = (index, attorney) => {
    if (attorney) {
      const updatedGuardians = [...data.guardians];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        name: attorney.label,
        address: attorney.address,
        city: attorney.city,
        state: attorney.state,
        zip_code: attorney.zip_code,
        is_attorney: "Yes",
        occupation: attorney.position,
        relationship: "Attorney of Record",
        citizenship: "USA",
        country: "USA",
        attorney: attorney.value,
        showAttorneyForm: false,
        is_attorney_home: "No", // Reset to office address by default
      };
      updateData({ ...data, guardians: updatedGuardians });
    } else {
      // Clear attorney selection
      const updatedGuardians = [...data.guardians];
      updatedGuardians[index] = {
        ...updatedGuardians[index],
        name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        is_attorney: "No",
        occupation: "",
        relationship: "",
        attorney: null,
        showAttorneyForm: false,
        is_attorney_home: "No",
      };
      updateData({ ...data, guardians: updatedGuardians });
    }
  };

  const handleGoogleCallback = (index, address, city, state, zipCode) => {
    // Use dataRef.current to get fresh state
    const currentData = dataRef.current;
    const updatedGuardians = Array.isArray(currentData.guardians) ? [...currentData.guardians] : [];
    updatedGuardians[index] = {
      ...updatedGuardians[index],
      address: address,
      city: city,
      state: state,
      zip_code: zipCode,
    };
    updateData({ ...currentData, guardians: updatedGuardians });
  };

  const handleZipCodeChange = async (e, index) => {
    const zipCode = e.target.value;
    const updatedGuardians = [...data.guardians];
    updatedGuardians[index] = { ...updatedGuardians[index], zip_code: zipCode };
    updateData({ ...data, guardians: updatedGuardians });

    // If ZIP code is 5 digits, fetch city and state
    if (zipCode.length === 5) {
      try {
        const response = await apiClient(`/fields/location/${zipCode}`, "GET");
        const { city, state } = response;
        // Use dataRef.current to get fresh state after async call
        const currentData = dataRef.current;
        const freshGuardians = Array.isArray(currentData.guardians) ? [...currentData.guardians] : [];
        freshGuardians[index] = {
          ...freshGuardians[index],
          city: city,
          state: state,
          zip_code: zipCode,
        };
        updateData({ ...currentData, guardians: freshGuardians });
      } catch (error) {
        console.error("Error fetching city/state by zip code:", error);
      }
    }
  };

  const formatSSN = (ssn) => {
    if (ssn.length < 4) return ssn;
    return "XXX-XX-" + ssn.slice(-4);
  };

  const renderGuardianFields = () => {
    const guardianFields = [];
    const numberOfGuardians = globalData.basicInformation?.PrincipalNumber || 1;

    // Ensure data.guardians is always an array
    const guardians = Array.isArray(data.guardians) ? data.guardians : [];

    for (let i = 0; i < numberOfGuardians; i++) {
      // Ensure guardian is initialized as an empty object if it does not exist at index i
      const guardian = guardians[i] || {};

      guardianFields.push(
        <Box key={`guardian${i}`} className="grid grid-cols-1 gap-4">
          {/* Dynamic Heading for Professional Guardian */}
          <Typography variant="h6" className="font-semibold mb-2">
            Principal #{i + 1}
          </Typography>

          {/* Is this Guardian a Professional Guardian? */}
          <TextField
            select
            label="Is Principal a Professional Guardian?"
            value={guardian.is_professional_guardian || "No"}
            disabled={isDisabled}
            onChange={(e) => {
              const value = e.target.value;
              const updatedGuardians = [...guardians];
              updatedGuardians[i] = {
                ...updatedGuardians[i],
                is_professional_guardian: value,
                showFullForm: value === "Yes" ? guardian.showFullForm : false,
                professional_guardian: null,
                is_new_pg: false,
                attorney: null,
              };
              updateData({ ...data, guardians: updatedGuardians });
            }}
            fullWidth
            required
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>

          {/* Is this Guardian an Attorney? (only shown if not professional guardian) */}
          {!(guardian.is_professional_guardian === "Yes") && (
            <TextField
              select
              label="Is Principal an Attorney?"
              value={guardian.is_attorney || "No"}
              disabled={isDisabled}
              onChange={(e) => {
                const value = e.target.value;
                const updatedGuardians = [...guardians];
                updatedGuardians[i] = {
                  ...updatedGuardians[i],
                  is_attorney: value,
                  attorney: null,
                  showAttorneyForm: false,
                };
                updateData({ ...data, guardians: updatedGuardians });
              }}
              fullWidth
              required
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          )}

          {/* Professional Guardian Autocomplete */}
          {guardian.is_professional_guardian === "Yes" &&
          !guardian.showFullForm ? (
            <Box sx={{ mt: 4 }}>
              <Autocomplete
                options={professionalGuardians}
                value={
                  professionalGuardians.find(
                    (pg) => pg.value === guardians[i].professional_guardian
                  ) || null
                }
                getOptionLabel={(option) => option.label || ""}
                loading={loadingGuardians}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Professional Guardian"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingGuardians ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      professional_guardian: newValue.value,
                      is_new_pg: false,
                    };
                    fetchProfessionalGuardianDetails(i, newValue.value);
                    updateData({ ...data, guardians: updatedGuardians });
                  }
                }}
              />
              {guardian.is_professional_guardian === "Yes" &&
                !guardian.showFullForm &&
                (() => {
                  const pg = professionalGuardians.find(
                    (pg) => pg.value === guardian.professional_guardian
                  );
                  return pg ? (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Selected Professional Guardian:
                      </Typography>
                      <Typography variant="body1">
                        <strong>Name:</strong> {pg.label}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Address:</strong> {pg.address}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Cell Phone:</strong> {pg.cell}
                      </Typography>
                    </Box>
                  ) : null;
                })()}
            <Typography
              variant="body2"
              className={`mt-2 ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 cursor-pointer'}`}
              onClick={() => {
                if (isDisabled) return;
                const updatedGuardians = [...guardians];
                updatedGuardians[i] = {
                  ...updatedGuardians[i],
                  showFullForm: true,
                  is_new_pg: true,
                };
                updateData({ ...data, guardians: updatedGuardians });
              }}
            >
              Professional Guardian not in the list?
            </Typography>
            </Box>
          ) : guardian.is_attorney === "Yes" && !guardian.showAttorneyForm ? (
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                options={attorneys}
                value={
                  attorneys.find(
                    (attorney) => attorney.value === guardian.attorney
                  ) || null
                }
                getOptionLabel={(option) => option.label || ""}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue || inputValue.length < 3) return [];
                  const input = inputValue.toLowerCase();
                  return options.filter((opt) =>
                    (opt.label || "").toLowerCase().includes(input)
                  );
                }}
                noOptionsText="Type at least 3 characters to search for attorneys"
                loading={loadingAttorneys}
                disabled={isDisabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Attorney"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAttorneys ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, newValue) =>
                  handleAttorneySelection(i, newValue)
                }
              />

              {guardian.is_attorney === "Yes" && guardian.attorney && (
                <>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      backgroundColor: "#f9f9f9",
                      boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Selected Attorney Details:
                    </Typography>
                    {(() => {
                      const selectedAttorney = attorneys.find(
                        (a) => a.value === guardian.attorney
                      );
                      return selectedAttorney ? (
                        <>
                          <Typography variant="body1">
                            <strong>Name:</strong> {selectedAttorney.label}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Position:</strong>{" "}
                            {selectedAttorney.position}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Office Address:</strong>{" "}
                            {selectedAttorney.address}
                          </Typography>
                          <Typography variant="body1">
                            <strong>City/State/Zip:</strong>{" "}
                            {`${selectedAttorney.city}, ${selectedAttorney.state} ${selectedAttorney.zip_code}`}
                          </Typography>
                        </>
                      ) : null;
                    })()}
                  </Box>

                  {/* Address Information Section */}
                  <Box sx={{ mb: 3, mt: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Step 1: Address Information
                    </Typography>
                    <TextField
                      select
                      label="Use attorney's home address?"
                      value={guardian.is_attorney_home || "No"}
                      onChange={(e) =>
                        handleAttorneyHomeAddressChange(i, e.target.value)
                      }
                      fullWidth
                      required
                      disabled={isDisabled}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                  </Box>

                  {/* Manual Address Fields (shown only when home address is selected) */}
                  {guardian.is_attorney_home === "Yes" && (
                    <Box className="grid grid-cols-1 gap-4" sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        Step 2: Enter Home Address:
                      </Typography>

                      {/* Google AutoComplete for Address */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "rgba(0, 0, 0, 0.6)",
                            marginBottom: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Search Address
                        </Typography>
                        <GoogleAutoComplete
                          placeholder="Search Home Address"
                          callback={(address, city, state, zipCode) =>
                            handleGoogleCallback(
                              i,
                              address,
                              city,
                              state,
                              zipCode
                            )
                          }
                        />
                      </Box>

                      <TextField
                        sx={{ mt: 1, mb: 1 }}
                        label="Street Address"
                        value={guardian.address || ""}
                        onChange={(e) => {
                          const updatedGuardians = [...guardians];
                          updatedGuardians[i] = {
                            ...updatedGuardians[i],
                            address: e.target.value,
                          };
                          updateData({ ...data, guardians: updatedGuardians });
                        }}
                        fullWidth
                        required
                        disabled={isDisabled}
                      />

                      {/* ZIP Code */}
                      <TextField
                        label="ZIP/Postal Code"
                        value={guardian.zip_code || ""}
                        onChange={(e) => handleZipCodeChange(e, i)}
                        fullWidth
                        required
                        disabled={isDisabled}
                      />

                      {/* City and State Dropdowns */}
                      {loadingCitiesStates ? (
                        <Box display="flex" justifyContent="center" my={3}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          <LabeledCreatableSelect
                            label="City"
                            options={allCities.map((city) => ({
                              label: city,
                              value: city,
                            }))}
                            value={
                              guardian.city
                                ? { label: guardian.city, value: guardian.city }
                                : null
                            }
                            onChange={(opt) => {
                              const updatedGuardians = [...guardians];
                              updatedGuardians[i] = {
                                ...updatedGuardians[i],
                                city: opt ? opt.value : "",
                              };
                              updateData({
                                ...data,
                                guardians: updatedGuardians,
                              });
                            }}
                            placeholder="Select or add a city"
                            disabled={isDisabled}
                          />
                          <LabeledCreatableSelect
                            label="State"
                            options={allStates.map((state) => ({
                              label: state,
                              value: state,
                            }))}
                            value={
                              guardian.state
                                ? {
                                    label: guardian.state,
                                    value: guardian.state,
                                  }
                                : null
                            }
                            onChange={(opt) => {
                              const updatedGuardians = [...guardians];
                              updatedGuardians[i] = {
                                ...updatedGuardians[i],
                                state: opt ? opt.value : "",
                              };
                              updateData({
                                ...data,
                                guardians: updatedGuardians,
                              });
                            }}
                            placeholder="Select or add a state"
                            disabled={isDisabled}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Box className="grid grid-cols-1 gap-4">
              {/* Name of Guardian */}
              <TextField
                sx={{ mt: 2, mb: 2 }}
                label="Name"
                value={guardian.name || ""}
                disabled={isDisabled}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    name: inputValue,
                    citizenship: "USA",
                    country: "USA",
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
                inputProps={{ maxLength: 255 }}
              />

              {(guardian.is_new_pg || globalData.basicInformation?.is_esignatures === "Yes") && (
                <>
                {/* Email requirement note */}
                  <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Email Required:</strong> {' '}
                      {guardian.is_new_pg && globalData.basicInformation?.is_esignatures === "Yes"
                        ? "Email is required for new professional guardians and for e-signature processing of indemnity agreements."
                        : guardian.is_new_pg
                        ? "Email is required for new professional guardians to create their account in the system."
                        : globalData.basicInformation?.is_esignatures === "Yes"
                        ? "Email is required for e-signature processing of indemnity agreements."
                        : "Email is required."}
                    </Typography>
                  </Box>
                <TextField
                  sx={{ mb: 2 }}
                  label="Email of Professional Guardian"
                  disabled={isDisabled}
                  value={guardian.email || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      email: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
                </>
              )}

              {/* Citizenship */}
              <Box>
                <TextField
                  select
                  label="Citizenship"
                  value={
                    guardian.citizenship === "Other"
                      ? "Other"
                      : guardian.citizenship || "USA"
                  }
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    if (e.target.value === "Other") {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        citizenship: "Other",
                      };
                    } else {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        citizenship: e.target.value,
                      };
                    }
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  disabled={isDisabled}
                  required
                >
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Pakistan">Pakistan</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="Other">Other (Please specify)</MenuItem>
                </TextField>

                {guardian.citizenship === "Other" && (
                  <TextField
                    label="Specify Citizenship"
                    value={guardian.custom_citizenship || ""}
                    onChange={(e) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        custom_citizenship: e.target.value,
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    fullWidth
                    required
                    placeholder="Enter citizenship"
                    disabled={isDisabled}
                  />
                )}
              </Box>

              {/* Country of Residence */}
              <Box>
                <TextField
                  select
                  label="Country of Residence"
                  disabled={isDisabled}
                  value={
                    guardian.country === "Other"
                      ? "Other"
                      : guardian.country || "USA"
                  }
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    if (e.target.value === "Other") {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        country: "Other",
                      };
                    } else {
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        country: e.target.value,
                      };
                    }
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                >
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="UK">UK</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Pakistan">Pakistan</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="Other">Other (Please specify)</MenuItem>
                </TextField>

                {guardian.country === "Other" && (
                  <TextField
                    label="Specify Country"
                    value={guardian.custom_country || ""}
                    onChange={(e) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        custom_country: e.target.value,
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    fullWidth
                    required
                    placeholder="Enter country"
                    disabled={isDisabled}
                  />
                )}
              </Box>

              {/* Relationship to Ward */}
              {!(guardian.is_professional_guardian === "Yes") && (
                <TextField
                  label="Relationship to Beneficiary"
                  value={guardian.relationship || ""}
                  disabled={isDisabled}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      relationship: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                />
              )}

              {/* Google AutoComplete for Address */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.6)",
                    marginBottom: "4px",
                    fontWeight: 500,
                  }}
                >
                  Search Address
                </Typography>
                <GoogleAutoComplete
                  placeholder="Search Address"
                  callback={(address, city, state, zipCode) =>
                    handleGoogleCallback(i, address, city, state, zipCode)
                  }
                />
              </Box>

              <TextField
                sx={{ mt: 1, mb: 1 }}
                label="Street Address"
                value={guardian.address || ""}
                onChange={(e) => {
                  const updatedGuardians = [...guardians];
                  updatedGuardians[i] = {
                    ...updatedGuardians[i],
                    address: e.target.value,
                  };
                  updateData({ ...data, guardians: updatedGuardians });
                }}
                fullWidth
                required
                disabled={isDisabled}
              />

              {/* ZIP Code */}
              <TextField
                label="ZIP/Postal Code"
                value={guardian.zip_code || ""}
                onChange={(e) => handleZipCodeChange(e, i)}
                fullWidth
                required
                disabled={isDisabled}
              />

              {/* City and State Dropdowns */}
              {loadingCitiesStates ? (
                <Box display="flex" justifyContent="center" my={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <LabeledCreatableSelect
                    label="City"
                    options={allCities.map((city) => ({
                      label: city,
                      value: city,
                    }))}
                    value={
                      guardian.city
                        ? { label: guardian.city, value: guardian.city }
                        : null
                    }
                    onChange={(opt) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        city: opt ? opt.value : "",
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    placeholder="Select or add a city"
                    disabled={isDisabled}
                  />
                  <LabeledCreatableSelect
                    label="State"
                    options={allStates.map((state) => ({
                      label: state,
                      value: state,
                    }))}
                    value={
                      guardian.state
                        ? { label: guardian.state, value: guardian.state }
                        : null
                    }
                    onChange={(opt) => {
                      const updatedGuardians = [...guardians];
                      updatedGuardians[i] = {
                        ...updatedGuardians[i],
                        state: opt ? opt.value : "",
                      };
                      updateData({ ...data, guardians: updatedGuardians });
                    }}
                    placeholder="Select or add a state"
                    disabled={isDisabled}
                  />
                </>
              )}

              {/* SSN and Occupation Fields */}
              {(globalData.basicInformation?.bondAmount >= 250000) && (role === "law_firm_user" || (role === "super_user" && pii_access) || (role === "internal_user" && pii_access)) && (
                <TextField
                  label="Social Security Number (SSN)"
                  type={pii_access && showSSNStates[i] ? "text" : "password"}
                  value={guardian.ssn || ""}
                  onChange={(e) => {
                    const updatedGuardians = [...guardians];
                    updatedGuardians[i] = {
                      ...updatedGuardians[i],
                      ssn: e.target.value,
                    };
                    updateData({ ...data, guardians: updatedGuardians });
                  }}
                  fullWidth
                  required
                  helperText={formatSSN(guardian.ssn || "")}
                  InputProps={{
                    endAdornment: pii_access ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle ssn visibility"
                          onClick={() => toggleShowSSN(i)}
                          edge="end"
                          disabled={isDisabled}
                        >
                          {showSSNStates[i] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              )}

              {(globalData.basicInformation?.bondAmount >= 250000) &&
                !(guardian.is_professional_guardian === "Yes") &&
                !(guardian.is_attorney === "Yes") && (
                  (() => {
                    const { employmentStatus, occupationText } =
                      splitEmploymentOccupation(guardian.occupation);

                    const questionLabel =
                      employmentStatus === "Retired"
                        ? "What occupation did you retire from?"
                        : employmentStatus === "Employed"
                          ? "What is your current occupation?"
                          : "Occupation";

                    return (
                      <>
                        <TextField
                          select
                          label="Employment Status"
                          value={employmentStatus || ""}
                          disabled={isDisabled}
                          onChange={(e) => {
                            const nextStatus = e.target.value;
                            const nextOccupation = joinEmploymentOccupation(
                              nextStatus,
                              occupationText
                            );
                            const updatedGuardians = [...guardians];
                            updatedGuardians[i] = {
                              ...updatedGuardians[i],
                              occupation: nextOccupation,
                            };
                            updateData({ ...data, guardians: updatedGuardians });
                          }}
                          fullWidth
                          required
                        >
                          <MenuItem value="Employed">Employed</MenuItem>
                          <MenuItem value="Unemployed">Unemployed</MenuItem>
                          <MenuItem value="Retired">Retired</MenuItem>
                        </TextField>

                        {(employmentStatus === "Employed" ||
                          employmentStatus === "Retired") && (
                          <TextField
                            label={questionLabel}
                            value={occupationText || ""}
                            disabled={isDisabled}
                            onChange={(e) => {
                              const nextText = e.target.value;
                              const nextOccupation = joinEmploymentOccupation(
                                employmentStatus,
                                nextText
                              );
                              const updatedGuardians = [...guardians];
                              updatedGuardians[i] = {
                                ...updatedGuardians[i],
                                occupation: nextOccupation,
                              };
                              updateData({ ...data, guardians: updatedGuardians });
                            }}
                            fullWidth
                            required
                          />
                        )}
                      </>
                    );
                  })()
                )}
            </Box>
          )}
        </Box>
      );
    }

    return guardianFields;
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Principals
      </Typography>

      {/* Render guardian fields */}
      {renderGuardianFields()}
    </Box>
  );
};

const AttorneyInformationForm = ({ globalData, data, updateData, role, isDisabled, isGuest }) => {
  const [attorneyInvolved, setAttorneyInvolved] = useState(
    data.attorneyInvolved || "Yes"
  );
  const [lawFirms, setLawFirms] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingAttorneys, setLoadingAttorneys] = useState(false);

  const [openAttorneyDialog, setOpenAttorneyDialog] = useState(false);
  const [openLawFirmDialog, setOpenLawFirmDialog] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);

  const navigate = useNavigate();

  const [newAttorney, setNewAttorney] = useState({ name: "", phone: "" });
  const [newLawFirm, setNewLawFirm] = useState({ name: "", phone: "" });
  const [newAddress, setNewAddress] = useState({
    address: "",
    zip: "",
    city: "",
    county: "",
    state: "",
    phone: "",
  });
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);

  // Set initial data for the newAttorney, newLawFirm, and newAddress if provided
  useEffect(() => {
    if (data.attorneyOfRecord) {
      setNewAttorney({
        name: data.attorneyOfRecordName || "",
        phone: data.attorneyOfRecordPhone || "",
      });
    }

    if (data.lawFirm) {
      setNewLawFirm({
        name: data.lawFirmName || "",
        phone: data.lawFirmPhone || "",
      });
    }

    if (data.lawFirmAddress) {
      setNewAddress({
        address: data.lawFirmAddress || "",
        zip: data.zip || "",
        city: data.city || "",
        county: data.county || "",
        state: data.state || "",
        phone: data.phone || "",
      });
    }
    if (data.attorneyInvolved) {
      setAttorneyInvolved(data.attorneyInvolved);
    }
  }, [data]);

  useEffect(() => {
    if (data.lawFirm && lawFirms.length > 0) {
      const selectedLawFirm = lawFirms.find(
        (firm) => firm.value === data.lawFirm
      );
      if (selectedLawFirm) {
        fetchLocationsByLawFirm(selectedLawFirm.value);
        fetchAttorneysByLawFirm(selectedLawFirm.value);
      }
    }
  }, [data.lawFirm, lawFirms]);

  const fetchLawFirms = async () => {
    setLoadingLawFirms(true);
    try {
      const response = await apiClient("/list-lawfirms/", "GET");
      const formattedLawFirms = response.map((firm) => ({
        label: firm.name,
        value: firm.lawfirm_id,
      }));
      setLawFirms(formattedLawFirms);
    } catch (error) {
      console.error("Error fetching law firms:", error);
    } finally {
      setLoadingLawFirms(false);
    }
  };

  useEffect(() => {
    fetchLawFirms();
  }, []);

  const fetchLocationsByLawFirm = async (lawFirmId) => {
    setLoadingLocations(true);
    try {
      const response = await apiClient(
        `/get-locations-by-lawfirm/${lawFirmId}`,
        "GET"
      );
      const formattedLocations = response.map((location) => ({
        label: location.name,
        value: location.law_firm_location_id,
        address: location.address,
        customer_number: location.customer_number,
      }));
      setLawFirmLocations(formattedLocations);
    } catch (error) {
      console.error("Error fetching law firm locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAttorneysByLawFirm = async (lawFirmId) => {
    setLoadingAttorneys(true);
    try {
      const response = await apiClient(
        `/lawfirm-user-location/${lawFirmId}`,
        "GET"
      );
      const filteredAttorneys = response
        .filter((user) => user.Position === "Attorney")
        .map((attorney) => ({
          label: attorney.lawfirm_user_name,
          value: attorney.lawfirm_user_id,
        }));
      setAttorneys(filteredAttorneys);
    } catch (error) {
      console.error("Error fetching attorneys:", error);
    } finally {
      setLoadingAttorneys(false);
    }
  };

  const handleLawFirmChange = (event, newValue) => {
    if (newValue) {
      updateData({
        ...data,
        lawFirm: newValue.value,
        attorneyInvolved: attorneyInvolved,
      });
      fetchLocationsByLawFirm(newValue.value);
      fetchAttorneysByLawFirm(newValue.value);
    } else {
      updateData({ ...data, lawFirm: "" });
      setLawFirmLocations([]);
      setAttorneys([]);
    }
  };

  const handleOpenAttorneyDialog = () => setOpenAttorneyDialog(true);
  const handleCloseAttorneyDialog = () => setOpenAttorneyDialog(false);
  const handleOpenLawFirmDialog = () => setOpenLawFirmDialog(true);
  const handleCloseLawFirmDialog = () => setOpenLawFirmDialog(false);
  const handleOpenAddressDialog = () => setOpenAddressDialog(true);
  const handleCloseAddressDialog = () => setOpenAddressDialog(false);

  const handleAddAttorney = async () => {
    try {
      setLoadingAttorneys(true);
      const response = await apiClient("/invite-lawfirm-user", "POST", {
        user: {
          email: `${newAttorney.name.toLowerCase().replaceAll(" ", "")}@test.com`,
        },
        law_firm_location_id: data.lawFirmAddress,
        lawfirm_id: data.lawFirm,
        lawfirm_user_name: newAttorney.name,
        preferred_name: newAttorney.name,
        title: "Attorney",
        position: "Attorney",
        is_legal_service: false,
        is_admin: false,
        middle_name: newAttorney.name,
        phone: newAttorney.phone,
        is_active: true,
      });
      updateData({ ...data, attorneyOfRecord: response.lawfirm_user_id });
      toast.success("Attorney added successfully!");
      handleCloseAttorneyDialog();
      fetchAttorneysByLawFirm(data.lawFirm); // Re-fetch attorneys and select the new one
    } catch (error) {
      console.error("Error adding attorney:", error);
      toast.error("Failed to add attorney!");
    } finally {
      setLoadingAttorneys(false);
    }
  };

  const handleAddLawFirm = async () => {
    try {
      setLoadingLawFirms(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm", "POST", {
        name: newLawFirm.name,
      });
      const matchedId = fuzzyRes?.match?.lawfirm_id ?? fuzzyRes?.law_firm?.id ?? fuzzyRes?.lawfirm_id;
      const matchedName = fuzzyRes?.match?.name ?? fuzzyRes?.law_firm?.name ?? fuzzyRes?.law_firm_name ?? "";
      const hasMatch = (fuzzyRes?.match != null && matchedId != null) || (fuzzyRes?.matched && matchedId != null);
      if (hasMatch) {
        setLoadingLawFirms(false);
        setFuzzyMatchResult({ id: matchedId, name: matchedName || newLawFirm.name });
        setFuzzyMatchDialogOpen(true);
        return;
      }
      const response = await apiClient("/create-a-lawfirm", "POST", {
        name: newLawFirm.name,
        website: "",
        logo: "",
        accounting_contact_full_name: "",
        accounting_contact_email: "",
        accounting_contact_phone: "",
        is_new: true,
        is_active: true,
        created_by_guest: true,
      });
      updateData({ ...data, lawFirm: response.lawfirm_id });
      toast.success("Law Firm added successfully!");
      handleCloseLawFirmDialog();
      fetchLawFirms();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    updateData({ ...data, lawFirm: fuzzyMatchResult.id });
    fetchLawFirms();
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    handleCloseLawFirmDialog();
    toast.success("Using existing law firm.");
  };

  const handleCreateNewLawFirmAnyway = async () => {
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoadingLawFirms(true);
      const response = await apiClient("/create-a-lawfirm", "POST", {
        name: newLawFirm.name,
        website: "",
        logo: "",
        accounting_contact_full_name: "",
        accounting_contact_email: "",
        accounting_contact_phone: "",
        is_new: true,
        is_active: true,
        created_by_guest: true,
      });
      updateData({ ...data, lawFirm: response.lawfirm_id });
      toast.success("Law Firm added successfully!");
      handleCloseLawFirmDialog();
      fetchLawFirms();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleAddAddress = async () => {
    try {
      setLoadingLocations(true);
      const response = await apiClient("/lawfirm-location/", "POST", [
        {
          lawfirm_id: data.lawFirm,
          name: "Guest Location",
          address: newAddress.address,
          zip_code: newAddress.zip,
          city: newAddress.city,
          county: newAddress.county,
          state: newAddress.state,
          phone: newAddress.phone,
          is_active: true,
        },
      ]);
      updateData({ ...data, lawFirmAddress: response[0].law_firm_location_id });
      toast.success("Address added successfully!");
      handleCloseAddressDialog();
      fetchLocationsByLawFirm(data.lawFirm);
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Attorney Information
      </Typography>

      <FormControl disabled={isDisabled} component="fieldset" sx={{ mb: 4 }}>
        <Typography variant="body1">
          Will an attorney remain involved throughout the duration of the case?
        </Typography>
        <RadioGroup
          row
          name="attorneyInvolved"
          value={attorneyInvolved}
          onChange={(e) => setAttorneyInvolved(e.target.value)}
          disabled={isDisabled}
        >
          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="No" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>

      {attorneyInvolved === "Yes" && (
        <Box className="grid grid-cols-1 gap-4">
          {/* Law Firm Autocomplete */}
          <Autocomplete
            options={lawFirms}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLawFirms}
            value={lawFirms.find((firm) => firm.value === data.lawFirm) || null}
            onChange={handleLawFirmChange}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                disabled={isDisabled}
                {...params}
                label="Law Firm"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLawFirms ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {globalData?.attorneyInformation?.is_guest && data.guest_lawfirm_name && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Guest Law Firm: {data.guest_lawfirm_name}
            </Typography>
          )}
          <Button
            disabled={isDisabled}
            variant="outlined"
            onClick={handleOpenLawFirmDialog}
            sx={{ mb: 2 }}
          >
            {loadingLawFirms ? (
              <CircularProgress size={24} />
            ) : (
              "Add New Law Firm"
            )}
          </Button>

          {/* Law Firm Locations */}
          <Autocomplete
            options={lawFirmLocations}
            disabled={isDisabled}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLocations}
            value={
              lawFirmLocations.find(
                (loc) => loc.value === data.lawFirmAddress
              ) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                lawFirmAddress: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                disabled={isDisabled}
                {...params}
                label="Law Firm Address"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

{(role === "internal_user" || role === "super_user") &&
            lawFirmLocations.find((loc) => loc.value === data.lawFirmAddress)
              ?.customer_number === null &&
            !isDisabled && (
              <Box
                sx={{
                  width: "100%",
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box sx={{ color: "warning.main", mt: 0.25 }}>
                  <WarningAmberIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    Location customer number
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    The location does not have a customer number. Please update it from Account Management (Law Firms). Otherwise system will automatically create a new law firm with a customer number in AMS.
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        navigate(
                          `/lawfirmlocationdetails/${data.lawFirmAddress}`
                        );
                      }}
                    >
                      Update Now
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

          {globalData?.attorneyInformation?.is_guest && data.guest_lawfirmaddress_name && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Guest Address: {data.guest_lawfirmaddress_name}
            </Typography>
          )}
          <Button
            disabled={isDisabled}
            variant="outlined"
            onClick={handleOpenAddressDialog}
            sx={{ mb: 2 }}
          >
            {loadingLocations ? (
              <CircularProgress size={24} />
            ) : (
              "Add New Address"
            )}
          </Button>

          {/* Attorney of Record */}
          <Autocomplete
            options={attorneys}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingAttorneys}
            disabled={isDisabled}
            value={
              attorneys.find(
                (attorney) => attorney.value === data.attorneyOfRecord
              ) || null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                attorneyOfRecord: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                disabled={isDisabled}
                {...params}
                label="Attorney of Record"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAttorneys ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {globalData?.attorneyInformation?.is_guest && data.guest_attorney_name && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Guest Attorney: {data.guest_attorney_name}
            </Typography>
          )}
          {data?.is_guest && (data?.referral_source || data?.referred_by) && (
            <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#f0f4ff", borderRadius: 1, border: "1px solid #d0d9f0" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1a237e", mb: 0.5 }}>
                Referral Information
              </Typography>
              {data.referral_source && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  How did you find us: {data.referral_source}
                </Typography>
              )}
              {data.referred_by && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Referred by: {data.referred_by}
                </Typography>
              )}
            </Box>
          )}
          <Button
            disabled={isDisabled}
            variant="outlined"
            onClick={handleOpenAttorneyDialog}
            sx={{ mb: 2 }}
          >
            {loadingAttorneys ? (
              <CircularProgress size={24} />
            ) : (
              "Add New Attorney"
            )}
          </Button>
        </Box>
      )}

      {/* Dialogs for Adding New Attorney, Law Firm, and Address */}
      <Dialog
        open={openAttorneyDialog}
        onClose={handleCloseAttorneyDialog}
        fullWidth
      >
        <DialogTitle>Add New Attorney</DialogTitle>
        <DialogContent>
          <TextField
            disabled={isDisabled}
            label="Attorney Name"
            name="name"
            value={newAttorney.name}
            onChange={(e) =>
              setNewAttorney({ ...newAttorney, name: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="Phone"
            name="phone"
            value={newAttorney.phone}
            onChange={(e) =>
              setNewAttorney({ ...newAttorney, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttorneyDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAttorney} color="primary">
            Add Attorney
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLawFirmDialog}
        onClose={handleCloseLawFirmDialog}
        fullWidth
      >
        <DialogTitle>Add New Law Firm</DialogTitle>
        <DialogContent>
          <TextField
            disabled={isDisabled}
            label="Law Firm Name"
            name="name"
            value={newLawFirm.name}
            onChange={(e) =>
              setNewLawFirm({ ...newLawFirm, name: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="Phone"
            name="phone"
            value={newLawFirm.phone}
            onChange={(e) =>
              setNewLawFirm({ ...newLawFirm, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLawFirmDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddLawFirm} color="primary">
            Add Law Firm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fuzzyMatchDialogOpen} onClose={() => { setFuzzyMatchDialogOpen(false); setFuzzyMatchResult(null); }}>
        <DialogTitle>Similar law firm found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar law firm was found: <strong>{fuzzyMatchResult?.name}</strong>. Do you want to use this existing firm or create a new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLawFirm} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLawFirmAnyway} color="secondary">
            Create new anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddressDialog}
        onClose={handleCloseAddressDialog}
        fullWidth
      >
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          <TextField
            disabled={isDisabled}
            label="Address"
            name="address"
            value={newAddress.address}
            onChange={(e) =>
              setNewAddress({ ...newAddress, address: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="ZIP"
            name="zip"
            value={newAddress.zip}
            onChange={(e) =>
              setNewAddress({ ...newAddress, zip: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="City"
            name="city"
            value={newAddress.city}
            onChange={(e) =>
              setNewAddress({ ...newAddress, city: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="County"
            name="county"
            value={newAddress.county}
            onChange={(e) =>
              setNewAddress({ ...newAddress, county: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="State"
            name="state"
            value={newAddress.state}
            onChange={(e) =>
              setNewAddress({ ...newAddress, state: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
          <TextField
            disabled={isDisabled}
            label="Phone"
            name="phone"
            value={newAddress.phone}
            onChange={(e) =>
              setNewAddress({ ...newAddress, phone: e.target.value })
            }
            fullWidth
            required
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAddress} color="primary">
            Add Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const RequestorInformationForm = ({
  data,
  bondData,
  updateData,
  role,
  isDisabled,
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0", // Light border for sleekness
      }}
    >
      {/* Heading */}
      <Typography variant="h5" className="font-bold mb-4">
        Requestor Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        <TextField
          disabled={isDisabled}
          label="Name"
          name="firstName" // Must match key in `data`
          value={data.firstName || ""}
          onChange={handleChange}
          fullWidth
          required
        />

        <TextField
          disabled={isDisabled}
          label="Email"
          name="email" // This is correct as it matches `data.email`
          value={data.email || ""}
          onChange={handleChange}
          fullWidth
          required
        />

        <InputMask
          mask="(999) 999-9999"
          value={data.phoneNumber || ""}
          onChange={handleChange}
          disabled={isDisabled}
        >
          {(inputProps) => (
            <TextField
              disabled={isDisabled}
              label="Phone Number"
              name="phoneNumber" // Must match key in `data`
              value={data.phoneNumber || ""}
              onChange={handleChange}
              fullWidth
              required
            />
          )}
        </InputMask>
      </Box>
    </Box>
  );
};

// Function to upload a file with file_name and fetch blob_link from the API
const uploadToADLS = async (file) => {
  try {
    // Prepare form data for the API
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_name", file.name); // Add file_name explicitly

    // Use the apiClient for the API call
    const response = await apiClient(
      "/bond-file-upload/", // Endpoint
      "POST", // Method
      formData // Data (formData for POST requests)
    );

    // Extract the blob_link from the response
    if (!response.blob_link) {
      throw new Error("Blob link not returned from the server.");
    }

    return response.blob_link;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

const DocumentUploadForm = ({
  data,
  updateData,
  globalData,
  role,
  pii_access,
  isDisabled,
}) => {
  const [formValues, setFormValues] = useState({
    courtOrder: null,
    lastWill: null,
    otherDocument: null,
    bondApp: null,
    bond: null,
    indemnity: null,
    cancellationDocument: null,
    endorsementDocument: null,
    creditDocument: null,
    fcraDocument: null,

    courtOrderLink: "",
    courtOrderFileName: "",
    lastWillLink: "",
    lastWillFileName: "",
    otherDocumentLink: "",
    otherDocumentFileName: "",
    bondAppLink: "",
    bondAppFileName: "",
    bondLink: "",
    bondFileName: "",
    indemnityLink: "",
    indemnityFileName: "",
    cancellationDocumentLink: "",
    cancellationDocumentFileName: "",
    endorsementDocumentLink: "",
    endorsementDocumentFileName: "",
    creditDocumentLink: "",
    creditDocumentFileName: "",
    fcraDocumentLink: "",
    fcraDocumentFileName: "",

    note: "",
  });

  const [loading, setLoading] = useState({
    courtOrder: false,
    lastWill: false,
    otherDocument: false,
    bondApp: false,
    bond: false,
    indemnity: false,
    cancellationDocument: false,
    endorsementDocument: false,
    creditDocument: false,
    fcraDocument: false,
  });

  useEffect(() => {
    if (data) {
      setFormValues((prevState) => ({
        ...prevState,
        courtOrderLink: data.courtOrderLink || "",
        lastWillLink: data.lastWillLink || "",
        otherDocumentLink: data.otherDocumentLink || "",
        bondAppLink: data.bondAppLink || "",
        bondLink: data.bondLink || "",
        indemnityLink: data.indemnityLink || "",
        cancellationDocumentLink: data.cancellationDocumentLink || "",
        endorsementDocumentLink: data.endorsementDocumentLink || "",
        creditDocumentLink: data.creditDocumentLink || "",

        courtOrderFileName: data.courtOrderFileName || "",
        lastWillFileName: data.lastWillFileName || "",
        otherDocumentFileName: data.otherDocumentFileName || "",
        bondAppFileName: data.bondAppFileName || "",
        bondFileName: data.bondFileName || "",
        indemnityFileName: data.indemnityFileName || "",
        cancellationDocumentFileName: data.cancellationDocumentFileName || "",
        endorsementDocumentFileName: data.endorsementDocumentFileName || "",
        creditDocumentFileName: data.creditDocumentFileName || "",
        fcraDocumentFileName: data.fcraDocumentFileName || "",
        fcraDocumentLink: data.fcraDocumentLink || "",

        note: data.note || "",
      }));
    }
  }, [data]);

  // Handle file input change and automatically upload
  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (!file) return;

    // Set the file in state and start loading
    setFormValues((prevState) => ({ ...prevState, [name]: file }));
    setLoading((prevState) => ({ ...prevState, [name]: true }));

    try {
      // Upload to ADLS and get the document link
      const uploadedLink = await uploadToADLS(file);

      // Set both file link and file name in the state
      setFormValues((prevState) => ({
        ...prevState,
        [name]: file,
        [`${name}Link`]: uploadedLink,
        [`${name}FileName`]: file.name,
      }));

      // Update the parent data with the new form values
      updateData({
        ...data,
        [`${name}Link`]: uploadedLink,
        [`${name}FileName`]: file.name,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading((prevState) => ({ ...prevState, [name]: false }));
    }
  };

  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pendingRemoveName, setPendingRemoveName] = useState(null);

  const handleRemoveClick = (name) => {
    setPendingRemoveName(name);
    setRemoveConfirmOpen(true);
  };

  const handleRemoveConfirm = () => {
    if (pendingRemoveName) {
      setFormValues((prevState) => ({
        ...prevState,
        [pendingRemoveName]: null,
        [`${pendingRemoveName}Link`]: "",
        [`${pendingRemoveName}FileName`]: "",
      }));
      updateData({
        ...data,
        [`${pendingRemoveName}Link`]: "",
        [`${pendingRemoveName}FileName`]: "",
      });
    }
    setRemoveConfirmOpen(false);
    setPendingRemoveName(null);
  };

  const handleRemoveCancel = () => {
    setRemoveConfirmOpen(false);
    setPendingRemoveName(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => {
      const updatedFormValues = { ...prevState, [name]: value };
      updateData(updatedFormValues);
      return updatedFormValues;
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const noFilePlaceholders = ["No file uploaded", "No file selected.", ""];
  const getFileName = (n) => formValues[`${n}FileName`] || data[`${n}FileName`];
  const hasFile = (n) =>
    formValues[n] ||
    formValues[`${n}Link`] ||
    data[`${n}Link`] ||
    (getFileName(n) && !noFilePlaceholders.includes(String(getFileName(n)).trim()));

  const renderDocumentSection = (label, name) => (
    <Box key={name}>
      <Typography variant="body1" className="mb-2">
        {label}
      </Typography>
      <Button
        disabled={isDisabled}
        variant="outlined"
        component="label"
        fullWidth
      >
        Browse...
        <input type="file" hidden name={name} onChange={handleFileChange} />
      </Button>
      <Typography variant="body2" color="textSecondary" mt={1}>
        {loading[name] ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <span>Uploading...</span>
          </Box>
        ) : hasFile(name) ? (
          formValues[name]?.name || getFileName(name)
        ) : (
          "No File Uploaded."
        )}
      </Typography>
      {hasFile(name) && (formValues[`${name}Link`] || data[`${name}Link`]) && (
        <Button
          variant="outlined"
          color="secondary"
          href={formValues[`${name}Link`] || data[`${name}Link`]}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          sx={{ mt: 2 }}
        >
          View Document
        </Button>
      )}
      {(formValues[name] || formValues[`${name}Link`] || data[`${name}Link`]) &&
       !["bond", "bondApp", "indemnity"].includes(name) && (
        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={() => handleRemoveClick(name)}
          disabled={isDisabled}
          sx={{ mt: 1 }}
        >
          Remove Attachment
        </Button>
      )}
    </Box>
  );

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Document Upload
      </Typography>

      <Box className="grid grid-cols-1 gap-6">
        {renderDocumentSection("Court Order", "courtOrder")}
        {renderDocumentSection("Last Will", "lastWill")}
        {renderDocumentSection("Other Document", "otherDocument")}
        {renderDocumentSection("Bond Application", "bondApp")}
        {renderDocumentSection("Bond Document", "bond")}
        {renderDocumentSection("Indemnity Agreement", "indemnity")}
        {renderDocumentSection("Cancellation Document", "cancellationDocument")}
        {renderDocumentSection("Endorsement Document", "endorsementDocument")}
        {renderDocumentSection("Endorsement POA Document", "endorsementPOADocument")}
        {(role === "carrier_team_member" || (role === "super_user" && pii_access) || (role === "internal_user" && pii_access)) &&
          renderDocumentSection("Credit Report Document", "creditDocument")
        }

        {renderDocumentSection("FCRA Document", "fcraDocument")}

        <TextField
          disabled={isDisabled}
          label="Note"
          name="note"
          value={formValues.note || ""}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          sx={{ mb: 3 }}
        />
      </Box>

      <Dialog open={removeConfirmOpen} onClose={handleRemoveCancel}>
        <DialogTitle>Remove Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const BillingInformationForm = ({
  globalData,
  data,
  updateData,
  role,
  isDisabled,
}) => {
  const [billingInfo, setBillingInfo] = useState({
    billing_entity: "",
    billing_lawfirmaddress_name: "",
    billing_principal_name: "",
    billing_other_name: "",
    billing_other_address: "",
    billing_other_relationship: "",
    billing_other_city: "",
    billing_other_state: "",
    billing_other_zip: "",
  });

  const [principalOptions, setPrincipalOptions] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);

  // Fetch principal options and law firm locations (reacts to law firm changes)
  useEffect(() => {
    const fetchPrincipalOptions = () => {
      const principals = globalData.guardianDetails?.guardians?.map(
        (guardian) => ({
          label:
            guardian.name +
            " - " +
            guardian.address +
            ", " +
            guardian.city +
            ", " +
            guardian.state +
            ", " +
            guardian.zip_code,
          value: guardian.name,
        })
      );
      setPrincipalOptions(principals || []);
    };

    const fetchLawFirmLocations = async () => {
      setLoading(true);
      try {
        const lawFirmId = globalData?.attorneyInformation?.lawFirm;
        if (!lawFirmId) {
          setLawFirmLocations([]);
          return;
        }
        const response = await apiClient(
          `/get-locations-by-lawfirm/${lawFirmId}`,
          "GET"
        );
        const formattedLocations = response.map((location) => ({
          label: location.name,
          value: location.law_firm_location_id,
          address: location.address,
        }));
        setLawFirmLocations(formattedLocations);
      } catch (error) {
        console.error("Error fetching law firm locations:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCitiesAndStates = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };

    fetchCitiesAndStates();
    fetchPrincipalOptions();
    fetchLawFirmLocations();
  }, [globalData.guardianDetails, globalData.attorneyInformation?.lawFirm]);

  // Guest bond + billing entity Law Firm: auto-select first location when law firm changes
  useEffect(() => {
    const isGuestBond = !!globalData?.attorneyInformation?.is_guest;
    if (!isGuestBond) return;
    if (billingInfo.billing_entity !== "Law Firm") return;
    if (!Array.isArray(lawFirmLocations) || lawFirmLocations.length === 0) return;

    const current = billingInfo.billing_lawfirmaddress_name;
    const stillValid = current && lawFirmLocations.some((l) => l.value === current);
    if (stillValid) return;

    const selectedLocation = lawFirmLocations[0]?.value || "";
    if (!selectedLocation) return;

    setBillingInfo((prev) => ({
      ...prev,
      billing_entity: "Law Firm",
      billing_lawfirmaddress_name: selectedLocation,
    }));
    updateData({
      billing_entity: "Law Firm",
      billing_lawfirmaddress_name: selectedLocation,
      billing_principal_name: "",
      billing_other_name: "",
      billing_other_address: "",
      billing_other_relationship: "",
      billing_other_city: "",
      billing_other_state: "",
      billing_other_zip: "",
      custNo: "",
    });
  }, [
    globalData?.attorneyInformation?.is_guest,
    globalData?.attorneyInformation?.lawFirm,
    billingInfo.billing_entity,
    billingInfo.billing_lawfirmaddress_name,
    lawFirmLocations,
    updateData,
  ]);

  // Sync form state with incoming data
  useEffect(() => {
    if (data) {
      setBillingInfo((prevState) => ({
        ...prevState,
        billing_entity: data.billing_entity || prevState.billing_entity,
        billing_lawfirmaddress_name:
          data.billing_lawfirmaddress_name ||
          prevState.billing_lawfirmaddress_name,
        billing_principal_name:
          data.billing_principal_name || prevState.billing_principal_name,
        billing_other_name:
          data.billing_other_name || prevState.billing_other_name,
        billing_other_address:
          data.billing_other_address || prevState.billing_other_address,
        billing_other_relationship:
          data.billing_other_relationship ||
          prevState.billing_other_relationship,
        billing_other_city:
          data.billing_other_city || prevState.billing_other_city,
        billing_other_state:
          data.billing_other_state || prevState.billing_other_state,
        billing_other_zip:
          data.billing_other_zip || prevState.billing_other_zip,
        custNo: data.custNo || prevState.custNo,
      }));
    }
  }, [data]);

  // Handle form field changes and update state dynamically
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Update the local state for the form
    const updatedBillingInfo = { ...billingInfo, [name]: value };
    setBillingInfo(updatedBillingInfo);

    // Update the parent component state immediately
    const updatedBillingData = {
      billing_entity: updatedBillingInfo.billing_entity,
      billing_lawfirmaddress_name:
        updatedBillingInfo.billing_entity === "Law Firm"
          ? updatedBillingInfo.billing_lawfirmaddress_name
          : "",
      billing_principal_name:
        updatedBillingInfo.billing_entity === "Principal"
          ? updatedBillingInfo.billing_principal_name
          : "",
      billing_other_name:
        updatedBillingInfo.billing_entity === "Other"
          ? updatedBillingInfo.billing_other_name
          : "",
      billing_other_address:
        updatedBillingInfo.billing_entity === "Other"
          ? updatedBillingInfo.billing_other_address
          : "",
      billing_other_relationship:
        updatedBillingInfo.billing_entity === "Other"
          ? updatedBillingInfo.billing_other_relationship
          : "",
      billing_other_city:
        updatedBillingInfo.billing_entity === "Other"
          ? updatedBillingInfo.billing_other_city
          : "",
      billing_other_state:
        updatedBillingInfo.billing_entity === "Other"
          ? updatedBillingInfo.billing_other_state
          : "",
      billing_other_zip:
        updatedBillingInfo.billing_entity === "Other"
          ? updatedBillingInfo.billing_other_zip
          : "",
      custNo:
        updatedBillingInfo.billing_entity === "Other" ||
        updatedBillingInfo.billing_entity === "Principal"
          ? updatedBillingInfo.custNo
          : "",
    };

    // Call the parent updateData function immediately to reflect changes in the parent component
    updateData(updatedBillingData);
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      {/* Heading */}
      <Typography variant="h5" className="font-bold mb-4">
        Billing Information
      </Typography>

      {/* Question: Who to Bill? */}
      <TextField
        disabled={isDisabled}
        select
        label="Who to Bill?"
        name="billing_entity"
        value={billingInfo.billing_entity}
        onChange={handleChange}
        fullWidth
        required
        sx={{ mb: 4 }}
      >
        <MenuItem value="Law Firm">Law Firm</MenuItem>
        <MenuItem value="Principal">Principal</MenuItem>
        <MenuItem value="Other">Other</MenuItem>
      </TextField>

      {/* Billing Locations for Law Firm */}
      {billingInfo.billing_entity === "Law Firm" && (
        <FormControl component="fieldset" sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select Billing Location:
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <RadioGroup
              name="billing_lawfirmaddress_name"
              value={billingInfo.billing_lawfirmaddress_name}
              onChange={handleChange}
            >
              {lawFirmLocations.map((location) => (
                <FormControlLabel
                  key={location.value}
                  value={location.value}
                  control={<Radio />}
                  label={`${location.label} - ${location.address}`}
                  disabled={isDisabled}
                />
              ))}
            </RadioGroup>
          )}
        </FormControl>
      )}

      {/* Billing Locations for Principal */}
      {billingInfo.billing_entity === "Principal" && (
        <>
          <FormControl component="fieldset" sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select Principal:
            </Typography>
            <RadioGroup
              name="billing_principal_name"
              value={billingInfo.billing_principal_name}
              onChange={handleChange}
              disabled={isDisabled}
            >
              {principalOptions.map((principal) => (
                <FormControlLabel
                  key={principal.value}
                  value={principal.value}
                  control={<Radio />}
                  label={principal.label}
                  disabled={isDisabled}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <TextField
            disabled={isDisabled}
            label="Customer Number"
            name="custNo"
            value={billingInfo.custNo}
            onChange={handleChange}
            fullWidth
            required
            sx={{ mb: 4 }}
          />
        </>
      )}

      {/* If "Other" is selected, show additional fields */}
      {billingInfo.billing_entity === "Other" && (
        <>
          <TextField
            label="Name"
            name="billing_other_name"
            value={billingInfo.billing_other_name}
            onChange={handleChange}
            disabled={isDisabled}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          <TextField
            label="Relationship"
            name="billing_other_relationship"
            value={billingInfo.billing_other_relationship}
            onChange={handleChange}
            disabled={isDisabled}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(0, 0, 0, 0.6)",
              marginBottom: "4px",
              fontWeight: 500,
            }}
          >
            Search Billing Address
          </Typography>

          <GoogleAutoComplete
            placeholder="Search Billing Address"
            callback={(address, city, state, zipCode) => {
              setBillingInfo((prev) => ({
                ...prev,
                billing_other_address: address,
                billing_other_city: city,
                billing_other_state: state,
                billing_other_zip: zipCode,
              }));
              updateData({
                ...data,
                billing_other_address: address,
                billing_other_city: city,
                billing_other_state: state,
                billing_other_zip: zipCode,
              });
            }}
          />

          <TextField
            label="Street Address"
            name="billing_other_address"
            value={billingInfo.billing_other_address || ""}
            disabled={isDisabled}
            onChange={handleChange}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />

          <TextField
            label="ZIP Code"
            name="billing_other_zip"
            value={billingInfo.billing_other_zip || ""}
            disabled={isDisabled}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
          />

          {loadingCitiesStates ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <LabeledCreatableSelect
                label="City"
                options={allCities.map((city) => ({
                  label: city,
                  value: city,
                }))}
                value={
                  billingInfo.billing_other_city
                    ? {
                        label: billingInfo.billing_other_city,
                        value: billingInfo.billing_other_city,
                      }
                    : null
                }
                onChange={(opt) => {
                  setBillingInfo((prev) => ({
                    ...prev,
                    billing_other_city: opt ? opt.value : "",
                  }));
                  updateData({
                    ...data,
                    billing_other_city: opt ? opt.value : "",
                  });
                }}
                placeholder="Select or add a city"
                disabled={isDisabled}
              />

              <LabeledCreatableSelect
                label="State"
                options={allStates.map((state) => ({
                  label: state,
                  value: state,
                }))}
                value={
                  billingInfo.billing_other_state
                    ? {
                        label: billingInfo.billing_other_state,
                        value: billingInfo.billing_other_state,
                      }
                    : null
                }
                onChange={(opt) => {
                  setBillingInfo((prev) => ({
                    ...prev,
                    billing_other_state: opt ? opt.value : "",
                  }));
                  updateData({
                    ...data,
                    billing_other_state: opt ? opt.value : "",
                  });
                }}
                placeholder="Select or add a state"
                disabled={isDisabled}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

const VermostTrusteeBondRetrieveForm = () => {
  const { bond_id } = useParams(); // Get bond_id from the URL
  const [isLoading, setIsLoading] = useState(false);
  const [isEsignatureLoading, setIsEsignatureLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [billingMethod, setBillingMethod] = useState("");
  const [isIndemnityReceived, setIsIndemnityReceived] = useState("");
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [endorsementLogs, setEndorsementLogs] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState(0);
  const [data, setData] = useState(null);
  const [bondUpdates, setBondUpdates] = useState([]);
  const { role } = useSelector((state) => state.auth);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectFcraFile, setRejectFcraFile] = useState(null);
  const [rejectFcraUploading, setRejectFcraUploading] = useState(false);
  const [creditReportFile, setCreditReportFile] = useState(null);
  const [isCreditUploading, setIsCreditUploading] = useState(false);
  const [editableCreatedAt, setEditableCreatedAt] = useState("");

  const isoToDatetimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const disabled = role !== "super_user" && role !== "internal_user";
  const dispatch = useDispatch();
  const username = useSelector((state) => state.auth?.user?.username) || "";

  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchCounts = async () => {
      try {
        const counts = await apiClient(`/messages-recent-by-sender/${bond_id}`, "GET");
        if (!isMounted) return;
        const total = Object.entries(counts || {}).reduce((sum, [senderType, count]) => {
          return senderType === role ? sum : sum + (Number(count) || 0);
        }, 0);
        setChatUnreadTotal(total);
      } catch (e) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [bond_id, role]);

  const [globalState, setGlobalState] = useState({
    basicInformation: {},
    generalInformation: {},
    guardianDetails: {},
    attorneyInformation: {},
    obligeeInformation: {},
    requestorInformation: {},
    documentUpload: {},
    billingInformation: {},
    bondNumber: {},
  });

  const getObligeeIdFromResponse = (data) => {
    if (data == null) return undefined;
    const raw = data.obligee ?? data.obligee_id ?? data.trustee_bond?.obligee;
    if (raw == null) return undefined;
    if (typeof raw === "number") return raw;
    if (typeof raw === "string" && /^\d+$/.test(String(raw).trim())) return Number(raw);
    if (typeof raw === "object" && raw !== null) return raw.obligee_id ?? raw.id ?? undefined;
    return undefined;
  };

  useEffect(() => {
    console.log(globalState);
  }, [globalState]);

  const userdetails = useSelector((state) => state.auth.user);

  // Fetch the bond data on component mount or whenever bond_id changes
  useEffect(() => {
    const fetchBondData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient(`/retrieve-trustee-bond/${bond_id}/`);
        setData(response);
        setEditableCreatedAt(isoToDatetimeLocal(response?.created_at) || "");
        setBillingMethod(response.trustee_bond.billing_method);
        setIsIndemnityReceived(
          response.trustee_bond?.is_indemnity_received === null
            ? ""
            : response.trustee_bond?.is_indemnity_received
            ? "Yes"
            : "No"
        );

        // Ensure that we have a valid response
        const data = response;
        if (!data) {
          throw new Error("No data found in the response");
        }


        // Safely extract and populate the state
        setGlobalState((prevState) => ({
          ...prevState,
          basicInformation: {
            legalName: data.trustee_bond?.legal_name || "",
            trustName: data.trustee_bond?.trust_name || "",
            bondSubCategory: data.bond_sub_category || "",
            dateOfBirth: data.trustee_bond?.dob || "",
            bondAmount: parseFloat(data.bond_amount).toString(),
            premiumAmount: data.premium,
            courtCaseNumber: data.court_case_number || "",
            PrincipalNumber: data.trustee_bond?.number_of_principals || "",
            is_esignatures: data?.is_esignatures
            ? "Yes"
            : "No",
            county: data.county,
          },
          generalInformation: {
            allUSUKCanada: data.trustee_bond?.all_us_canadian_uk_citizens
              ? "Yes"
              : "No",
            ongoingBusiness: data.trustee_bond?.any_business_operated
              ? "Yes"
              : "No",
            indebtedGuardians: data.trustee_bond?.indebted_state ? "Yes" : "No",
            replacingPriorBond: data.trustee_bond?.is_replaced_prior
              ? "Yes"
              : "No",
            jointControlExercised: data.trustee_bond?.joint_control_exercised
              ? "Yes"
              : "No",
            supplementalNeedsTrust: data.trustee_bond?.is_supplemental_needs
              ? "Yes"
              : "No",
            requestForAuthorization: data.trustee_bond?.credit_report_allowed
              ? "Yes"
              : "No",
            explanations: {
              ongoingBusiness:
                data.trustee_bond?.any_business_operated_explanation || "",
              indebtedGuardians:
                data.trustee_bond?.indebted_state_explanation || "",
              replacingPriorBond:
                data.trustee_bond?.is_replaced_prior_explanation || "",
              supplementalNeedsTrust:
                data.trustee_bond?.supplemental_needs_trust_explanation || "",
            },
          },
          guardianDetails: {
            guardians: data.trustees.map((guardian) => ({
              trustee_id: guardian.trustee_id,
              name: guardian.name,
              citizenship: guardian.citizenship,
              custom_citizenship: guardian.custom_citizenship,
              country: guardian.country,
              custom_country: guardian.custom_country,
              relationship: guardian.relationship,
              address: guardian.address,
              guardian_street_address_2: "",
              city: guardian.city,
              state: guardian.state,
              zip_code: guardian.zip_code,
              occupation: guardian.occupation,
              ssn: guardian.ssn,
              is_professional_guardian: guardian.is_professional_guardian
                ? "Yes"
                : "No",
              professional_guardian: guardian.professional_guardian,
              is_attorney: guardian.is_attorney ? "Yes" : "No",
              is_attorney_home: guardian.is_attorney_home ? "Yes" : "No",
              attorney: guardian.attorney,
            })),
          },
          attorneyInformation: {
            attorneyInvolved: data.law_firm || data.guest_lawfirm_name ? "Yes" : "No",
            lawFirm: data.law_firm,
            lawFirmAddress: data.law_firm_location,
            attorneyOfRecord: data.law_firm_user,
            guest_lawfirm_name: data.guest_lawfirm_name,
            guest_lawfirmaddress_name: data.guest_lawfirmaddress_name,
            guest_attorney_name: data.guest_attorney_name,
            is_guest: data.is_guest,
            referral_source: data.referral_source,
            referred_by: data.referred_by,
          },
          obligeeInformation: (() => {
            const apiObligeeId = getObligeeIdFromResponse(data);
            console.log("[TrusteeBondRetrieve] API obligee from response:", data.obligee, data.trustee_bond?.obligee, "-> extracted id:", apiObligeeId);
            return { obligeeId: apiObligeeId };
          })(),
          requestorInformation: {
            attorneyInvolved: data.law_firm ? "Yes" : "No",
            email: data.requester_user_email || "No email",
            phoneNumber: data.requester_phone_number || "No phone number",
            firstName: data.requester_name,
          },
          documentUpload: {
            lastWillFileName:
              data.trustee_bond?.copy_of_last_will_and_testament ||
              "No file uploaded",
            lastWillLink:
              data.trustee_bond?.copy_of_last_will_and_testament_link || "",
            otherDocumentFileName:
              data.trustee_bond?.copy_of_supplemental_needs_trust ||
              "No file uploaded",
            otherDocumentLink:
              data.trustee_bond?.copy_of_supplemental_needs_trust_link || "",
            courtOrderFileName:
              data.trustee_bond?.copy_of_court_order || "No file uploaded",
            courtOrderLink: data.trustee_bond?.copy_of_court_order_link || "",
            bondAppFileName: data.first_document_name || "No file uploaded",
            bondAppLink: data.first_document_url || "",
            bondFileName: data.second_document_name || "No file uploaded",
            bondLink: data.second_document_url || "",
            indemnityFileName: data.third_document_name || "No file uploaded",
            indemnityLink: data.third_document_url || "",
            cancellationDocumentFileName:
              data.trustee_bond?.cancellation_document || "No file uploaded",
            cancellationDocumentLink:
              data.trustee_bond?.cancellation_document_link || "",
            endorsementDocumentFileName:
              data.trustee_bond?.endorsement_document || "No file uploaded",
            endorsementDocumentLink:
              data.trustee_bond?.endorsement_document_link || "",
            endorsementPOADocumentFileName:
              data.trustee_bond?.endorsement_poa_document || "No file uploaded",
            endorsementPOADocumentLink:
              data.trustee_bond?.endorsement_poa_document_link || "",
            creditDocumentFileName:
              data.trustee_bond?.credit_report_document || "No file uploaded",
            creditDocumentLink:
              data.trustee_bond?.credit_report_document_link || "",
            fcraDocumentFileName:
              data.trustee_bond?.fcra_document || "No file uploaded",
            fcraDocumentLink:
              data.trustee_bond?.fcra_document_link || "",
            note: data.trustee_bond?.notes || "",
          },
          billingInformation: {
            billing_entity: data.trustee_bond?.billing_entity || "Unknown",
            billing_lawfirmaddress_name:
              data.trustee_bond?.billing_lawfirmaddress_name || "",
            billing_principal_name:
              data.trustee_bond?.billing_principal_name || "Unknown",
            billing_other_name: data.trustee_bond?.billing_other_name || "",
            billing_other_address:
              data.trustee_bond?.billing_other_address || "",
            billing_other_relationship:
              data.trustee_bond?.billing_other_relationship || "",
            billing_other_city: data.trustee_bond?.billing_other_city,
            billing_other_state: data.trustee_bond?.billing_other_state,
            billing_other_zip: data.trustee_bond?.billing_other_zip,
            billing_method: data.trustee_bond?.billing_method || data.billing_method || "",
            custNo: data.trustee_bond?.other_custno || "",
          },
          bondNumber: {
            bond_number: data.bond_number,
          },
        }));
      } catch (error) {
        console.error("Error fetching bond data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBondData();
  }, [bond_id]);

  const fetchEndorsementLogs = async () => {
    try {
      setIsHistoryLoading(true);
      const response = await apiClient(`/endorsement-logs/${bond_id}/`);
      setEndorsementLogs(response);
    } catch (error) {
      toast.error("Failed to load endorsement history");
      console.error(error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const toggleHistoryDrawer = async () => {
    if (!isHistoryOpen) {
      await fetchEndorsementLogs();
    }
    setIsHistoryOpen((prev) => !prev);
  };

  const generatePayload = (globalState, status) => {
    return {
      bond_type: 4,
      bond_sub_category: globalState.basicInformation?.bondSubCategory,
      bond_amount: globalState.basicInformation?.bondAmount,
      bondNumber: globalState.bondNumber?.bond_number?.bondno_id ?? null,
      premium: globalState.basicInformation?.premiumAmount,
      court_case_number: globalState.basicInformation?.courtCaseNumber,
      state: 6,
      bond_carrier: is_prod ? 7 : 4,
      county: globalState.basicInformation?.county,
      is_esignatures: globalState.basicInformation?.is_esignatures === "Yes",
      status: status,
      updated_at: new Date().toISOString(),
      law_firm: globalState.attorneyInformation?.lawFirm,
      law_firm_location: globalState.attorneyInformation?.lawFirmAddress,
      law_firm_user: globalState.attorneyInformation?.attorneyOfRecord,
      obligee: globalState.obligeeInformation?.obligeeId,
      requester_user_email: globalState.requestorInformation?.email,
      requester_phone_number: globalState.requestorInformation?.phoneNumber,
      billing_method: billingMethod || "",
      name: globalState.basicInformation?.legalName,
      requester_name: globalState.requestorInformation?.firstName,
      trustee_bond: {
        county: globalState.basicInformation?.county,
        amount: globalState.basicInformation?.amount,
        court_file_no: globalState.basicInformation?.courtCaseNumber,
        legal_name: globalState.basicInformation?.legalName,
        trust_name: globalState.basicInformation?.trustName,
        dob: globalState.basicInformation?.dateOfBirth,
        number_of_principals: globalState.basicInformation?.PrincipalNumber,
        all_us_canadian_uk_citizens:
          globalState.generalInformation?.allUSUKCanada === "Yes",
        all_us_canadian_uk_citizens_explanation: "",
        any_business_operated:
          globalState.generalInformation?.ongoingBusiness === "Yes",
        any_business_operated_explanation:
          globalState.generalInformation?.explanations?.ongoingBusiness,
        indebted_state:
          globalState.generalInformation?.indebtedGuardians === "Yes",
        indebted_state_explanation:
          globalState.generalInformation?.explanations?.indebtedGuardians,
        is_replaced_prior:
          globalState.generalInformation?.replacingPriorBond === "Yes",
        is_replaced_prior_explanation:
          globalState.generalInformation?.explanations?.replacingPriorBond,
        is_supplemental_needs:
          globalState.generalInformation?.supplementalNeedsTrust === "Yes",
        copy_of_court_order: globalState.documentUpload?.courtOrderFileName,
        copy_of_court_order_link: globalState.documentUpload?.courtOrderLink,
        other_document: globalState.documentUpload?.otherDocumentFileName,
        other_document_link: globalState.documentUpload?.otherDocumentLink,
        cancellation_document:
          globalState.documentUpload?.cancellationDocumentFileName,
        cancellation_document_link:
          globalState.documentUpload?.cancellationDocumentLink,
        endorsement_document:
          globalState.documentUpload?.endorsementDocumentFileName,
        endorsement_document_link:
          globalState.documentUpload?.endorsementDocumentLink,
        endorsement_poa_document:
          globalState.documentUpload?.endorsementPOADocumentFileName,
        endorsement_poa_document_link:
          globalState.documentUpload?.endorsementPOADocumentLink,
        credit_report_document:
          globalState.documentUpload?.creditDocumentFileName,
        credit_report_document_link:
          globalState.documentUpload?.creditDocumentLink,
        fcra_document: globalState.documentUpload?.fcraDocumentFileName,
        fcra_document_link: globalState.documentUpload?.fcraDocumentLink,
        billing_entity: globalState.billingInformation?.billing_entity,
        billing_lawfirmaddress_name:
          globalState.billingInformation?.billing_lawfirmaddress_name,
        billing_principal_name:
          globalState.billingInformation?.billing_principal_name,
        billing_other_name: globalState.billingInformation?.billing_other_name,
        billing_other_address:
          globalState.billingInformation?.billing_other_address,
        billing_other_city:
          globalState.billingInformation?.billing_other_city || "",
        billing_other_state:
          globalState.billingInformation?.billing_other_state || "",
        billing_other_zip:
          globalState.billingInformation?.billing_other_zip || "",
        billing_other_relationship:
          globalState.billingInformation?.billing_other_relationship,
        billing_method: globalState.billingInformation?.billing_method || billingMethod || "",
        other_custno: globalState.billingInformation?.custNo,
        credit_report_allowed:
          globalState.generalInformation?.requestForAuthorization === "Yes",
        is_indemnity_received:
          isIndemnityReceived === "" ? null : isIndemnityReceived === "Yes",
      },

      trustees: (globalState.guardianDetails?.guardians || []).slice(0, globalState.basicInformation?.PrincipalNumber),
    };
  };

  const handleFinish = async () => {
    setIsSubmitLoading(true);
    const payload = generatePayload(globalState, data.status);
    if (editableCreatedAt) {
      payload.created_at = new Date(editableCreatedAt).toISOString();
    }

    // IMPORTANT: "Update" must NOT assign a new bond number.
    // It may only change bond number if one already exists.
    const { bondNumber, ...payloadWithoutBondNumber } = payload;

    const currentBondNumber =
      data?.bond_number?.bondno_id != null ? Number(data.bond_number.bondno_id) : null;
    const newBondNumber =
      globalState.bondNumber?.bond_number?.bondno_id != null
        ? Number(globalState.bondNumber.bond_number.bondno_id)
        : null;

    if (
      currentBondNumber != null &&
      newBondNumber != null &&
      currentBondNumber !== newBondNumber
    ) {
      try {
        await updateBondNumber(
          newBondNumber,
          Number(bond_id),
          globalState.attorneyInformation?.lawFirm
        );
      } catch (error) {
        console.error("API call error:", error);
        if (error.response?.data?.error) {
          console.error("Error response data:", error.response.data);
          toast.error(`Bond number error: ${error.response.data.error}`);
        } else {
          toast.error("Failed to update bond number.");
        }
      }
    }

    try {
      await apiClient(`/update_trustee_bond/${bond_id}/`, "PATCH", payloadWithoutBondNumber);
      toast.success("Bond updated successfully!");
      dispatch(increment());
    } catch (error) {
      console.error("Error updating the bond request:", error);
      const message = extractErrorMessage(error);
      toast.error(`Failed to update bond. ${message}`);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleSignIndemnity = async () => {
    setIsEsignatureLoading(true);
    try {
      await apiClient(
        `/bond-signature/${bond_id}/`,
        "POST",
      );
      toast.success("E-signature request sent successfully");
    } catch (error) {
      toast.error("Failed to send e-signature request.");
      console.error(error);
    } finally {
      setIsEsignatureLoading(false);
    }
  };

  const updateGlobalState = (stepKey, stepData) => {
    setGlobalState((prevState) => ({
      ...prevState,
      [stepKey]: stepData,
    }));
  };

  const handleApprove = async (options) => {
    setIsApproveLoading(true);
    const payload = generatePayload(globalState, "Approved");
    const { bondNumber, ...payloadWithoutBondNumber } = payload;

    // Add ams_flow as boolean to both payload objects
    payload.ams_flow = options.ams_flow;
    payload.billing_method = options.billing_method;

    const currentBondNumber = Number(data.bond_number?.bondno_id || null);
    const newBondNumber = Number(globalState.bondNumber?.bond_number?.bondno_id || null);

    try {
      // PATCH request to update the guardianship bond using the bond_id
      const response_update = await apiClient(
        `/update_trustee_bond/${bond_id}/`,
        "PATCH",
        payloadWithoutBondNumber
      );
      if (response_update.bond.bond_number === null || currentBondNumber !== newBondNumber) {
        await updateBondNumber(
          Number(globalState.bondNumber.bond_number.bondno_id),
          Number(bond_id),
          globalState.attorneyInformation?.lawFirm
        );
      }
      await apiClient(
        `/approve_trustee_bond/${bond_id}/`,
        "POST",
        payload
      );
      setIsApproveLoading(false);
      toast.success("Bond Approved Successfully");
      dispatch(increment());
      setTimeout(function () {
        window.close();
      }, 2000);
    } catch (error) {
      setIsApproveLoading(false);
      const message = extractErrorMessage(error);
      toast.error(`Bond approval failed. ${message}`);
      console.error("Error updating the bond request:", error);
    }
  };

  const handleReject = async (fcraOverride) => {
    setIsRejectLoading(true);
    const payload = generatePayload(globalState, "Rejected");
    if (fcraOverride?.fcraDocumentFileName != null || fcraOverride?.fcraDocumentLink != null) {
      if (payload.trustee_bond) {
        payload.trustee_bond.fcra_document = fcraOverride.fcraDocumentFileName ?? payload.trustee_bond.fcra_document;
        payload.trustee_bond.fcra_document_link = fcraOverride.fcraDocumentLink ?? payload.trustee_bond.fcra_document_link;
      }
    }

    try {
      await apiClient(
        `/update_trustee_bond/${bond_id}/`,
        "PATCH",
        payload
      );
      setIsRejectLoading(false);
      setRejectDialogOpen(false);
      setRejectFcraFile(null);
      toast.success("Bond Rejected Successfully");
      dispatch(increment());
      setTimeout(function () {
        window.close();
      }, 2000);
    } catch (error) {
      setIsRejectLoading(false);
      console.error("Error updating the bond request:", error);
    }
  };

  const [isUnderwritingLoading, setIsUnderwritingLoading] = useState(false);

  const handleUpdateUnderwriting = async (status) => {
    try {
      // Set the correct loading state based on the status
      if (status === "approved") {
        setIsApproveLoading(true); // Set approve loading state
      } else if (status === "rejected") {
        setIsRejectLoading(true); // Set reject loading state
      }
      setIsUnderwritingLoading(true); // Set general underwriting loading state

      // Call the API to update the underwriting status
      await apiClient(
        `/update_underwriting_status/${bond_id}/`,
        "PATCH",
        { status: status }
      );

      // Show success toast with dynamic status
      toast.success(`Underwriting ${status} successfully!`);
    } catch (error) {
      // Show error toast with dynamic status
      toast.error(`Failed to ${status} underwriting. Please try again.`);
    } finally {
      // Reset all loading states
      setIsApproveLoading(false);
      setIsRejectLoading(false);
      setIsUnderwritingLoading(false);
    }
  };

  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOptionSelect = (options) => {
    if (options.ams_flow) {
      // Approving WITH invoice → billing method must be selected
      if (!billingMethod) {
        toast.error("Please select a Billing Method before Approval!  ");
        handleMenuClose();
        return;
      }
    }

    // Pass the billing method only if ams_flow is true
    handleApprove({
      ams_flow: options.ams_flow,
      billing_method: billingMethod || "Direct Bill",
    });
    handleMenuClose();
  };

  const [underwritingAnchorEl, setUnderwritingAnchorEl] = React.useState(null);
  // Handlers for underwriting dropdown
  const handleUnderwritingMenuClick = (event) => {
    setUnderwritingAnchorEl(event.currentTarget);
  };

  const handleUnderwritingMenuClose = () => {
    setUnderwritingAnchorEl(null);
  };

  const [overrideReasonDialogOpen, setOverrideReasonDialogOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [manualUnderwritingDialogOpen, setManualUnderwritingDialogOpen] = useState(false);
  const [manualUnderwritingReason, setManualUnderwritingReason] = useState("");
  const handleOverrideUnderwritingClick = () => {
    handleUnderwritingMenuClose();
    setOverrideReason("");
    setOverrideReasonDialogOpen(true);
  };
  const handleManualUnderwritingClick = () => {
    handleUnderwritingMenuClose();
    setManualUnderwritingReason("");
    setManualUnderwritingDialogOpen(true);
  };
  const handleSubmitManualUnderwriting = async () => {
    const reason = (manualUnderwritingReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason for creating the underwriting entry.");
      return;
    }
    try {
      setIsUnderwritingLoading(true);
      if (!userdetails?.first_name || !userdetails?.last_name) {
        throw new Error("User information is incomplete");
      }
      await apiClient(
        `/trigger_underwriting/${bond_id}/`,
        "POST",
        {
          underwriting_reason: reason,
          created_by: `${userdetails.first_name} ${userdetails.last_name}`,
        },
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Underwriting entry created successfully.");
      setManualUnderwritingDialogOpen(false);
      setManualUnderwritingReason("");
    } catch (error) {
      console.error("Create underwriting entry error:", error);
      const errorMessage =
        error.detail ||
        error.message ||
        (error.response && error.response.data && error.response.data.detail) ||
        "Failed to create underwriting entry. Please try again.";
      toast.error(errorMessage);
      if (error.response?.status === 401) return;
    } finally {
      setIsUnderwritingLoading(false);
    }
  };
  const handleSubmitOverrideReason = async () => {
    const reason = (overrideReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason for overriding underwriting.");
      return;
    }
    try {
      setIsUnderwritingLoading(true);
      if (!userdetails?.first_name || !userdetails?.last_name) {
        throw new Error("User information is incomplete");
      }
      await apiClient(
        `/override_underwriting/${bond_id}/`,
        "POST",
        {
          overrided_by: `${userdetails.first_name} ${userdetails.last_name}`,
          override_reason: reason,
        },
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Underwriting overridden successfully");
      setOverrideReasonDialogOpen(false);
      setOverrideReason("");
    } catch (error) {
      console.error("Override underwriting error:", error);
      const errorMessage =
        error.detail ||
        error.message ||
        (error.response && error.response.data && error.response.data.detail) ||
        "Failed to override underwriting. Please try again.";
      toast.error(errorMessage);
      if (error.response?.status === 401) return;
    } finally {
      setIsUnderwritingLoading(false);
    }
  };

  const runCreditReport = async (bondId, file) => {
    const formData = new FormData();
    formData.append("credit_report_document", file);

    return await apiClient(
      `/run-credit-report/?bond_id=${bondId}`,
      "POST",
      formData,
      {},
      {
        "Content-Type": "multipart/form-data",
      }
    );
  };

  const [isDataCorrectionOpen, setIsDataCorrectionOpen] = useState(false);
  const [dataCorrectionTab, setDataCorrectionTab] = useState(0);
  const [bondDataCorrectionsList, setBondDataCorrectionsList] = useState([]);
  const [bondDataCorrectionsLoading, setBondDataCorrectionsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [dataCorrectionForm, setDataCorrectionForm] = useState({
    county: data?.county || "",
    courtCaseNumber: data?.court_case_number || "",
    bondAmount: data?.bond_amount || "",
    premium: data?.premium || "",
    reason: "",
    legalName: data?.trustee_bond?.legal_name || "",
    trustName: data?.trustee_bond?.trust_name || "",
    trustees:
      data?.trustees?.length > 0
        ? data.trustees
        : [
            {
              trustee_id: null,
              name: "",
              citizenship: "",
              custom_citizenship: "",
              relationship: "",
              address: "",
              city: "",
              state: "",
              zip_code: "",
              occupation: "",
              ssn: "",
              is_professional_guardian: false,
              professional_guardian: null,
              is_attorney: false,
              is_attorney_home: false,
              attorney: null,
            },
          ],
  });

  const fetchCounties = async () => {
    try {
      setLoadingCounties(true);
      const response = await apiClient(`/counties-by-state/6/`, "GET"); // Adjust the endpoint and state ID as needed
      const formattedCounties = response.map((county) => county.bond_county);
      setCounties(formattedCounties);
    } catch (error) {
      console.error("Error fetching counties:", error);
    } finally {
      setLoadingCounties(false);
    }
  };

  // Fetch bond data corrections when "Correction history" tab is selected
  useEffect(() => {
    if (!isDataCorrectionOpen || dataCorrectionTab !== 1 || !bond_id) return;
    setBondDataCorrectionsLoading(true);
    apiClient(`/bond-data-corrections/bond/${bond_id}/`, "GET", null, { limit: 100, offset: 0 })
      .then((res) => setBondDataCorrectionsList(Array.isArray(res) ? res : []))
      .catch((e) => { if (e?.status !== 404) console.error(e); setBondDataCorrectionsList([]); })
      .finally(() => setBondDataCorrectionsLoading(false));
  }, [isDataCorrectionOpen, dataCorrectionTab, bond_id]);

  const handleOpenDataCorrection = () => {
    fetchCounties(); // Fetch the list of counties
    setDataCorrectionForm({
      county: data?.county || "",
      courtCaseNumber: data?.court_case_number || "",
      bondAmount: data?.bond_amount || "",
      premium: data?.premium || "",
      reason: "",
      legalName: data?.trustee_bond?.legal_name || "",
      trustName: data?.trustee_bond?.trust_name || "",
      aliases: data?.trustee_bond?.alias_trustee_legal_name || "",
      trustees:
        data?.trustees?.length > 0
          ? data.trustees
          : [
              {
                trustee_id: null,
                name: "",
                citizenship: "",
                custom_citizenship: "",
                relationship: "",
                address: "",
                city: "",
                state: "",
                zip_code: "",
                occupation: "",
                ssn: "",
                is_professional_guardian: false,
                professional_guardian: null,
                is_attorney: false,
                is_attorney_home: false,
                attorney: null,
              },
            ],
    });
    setIsDataCorrectionOpen(true);
  };

  const handleBondAmountChange = (e) => {
    const newBondAmount = e.target.value;
    const formattedAmount = Math.round(parseFloat(newBondAmount));
    const newPremium = calculatePremium(formattedAmount); // Implement this function as needed
    setDataCorrectionForm((prev) => ({
      ...prev,
      bondAmount: formattedAmount,
      premium: newPremium,
    }));
  };

  const handleSubmitDataCorrection = async () => {
    try {
      if (!dataCorrectionForm.reason) {
        toast.error("Please provide a reason for the change");
        return;
      }

      setIsSubmitting(true);

      const payload = {
        county: dataCorrectionForm.county,
        court_case_number: dataCorrectionForm.courtCaseNumber,
        bond_amount: dataCorrectionForm.bondAmount,
        premium: dataCorrectionForm.premium,
        trustees: dataCorrectionForm.trustees,
        trustee_bond: {
          legal_name: dataCorrectionForm.legalName,
          trust_name: dataCorrectionForm.trustName,
          alias_trustee_legal_name: dataCorrectionForm.aliases,
          notes: `${data.trustee_bond?.notes || ""}${data.trustee_bond?.notes ? "\n\n" : ""}Reason for Change: ${dataCorrectionForm.reason}`,
        },
      };

      // Compute changed fields
      const changes = [];
      if ((dataCorrectionForm.county || "") !== (data?.county || "")) {
        changes.push({ field: "County", oldValue: data?.county || "", newValue: dataCorrectionForm.county || "" });
      }
      if ((dataCorrectionForm.courtCaseNumber || "") !== (data?.court_case_number || "")) {
        changes.push({ field: "Court Case Number", oldValue: data?.court_case_number || "", newValue: dataCorrectionForm.courtCaseNumber || "" });
      }
      if (Number(dataCorrectionForm.bondAmount || 0) !== Number(data?.bond_amount || 0)) {
        changes.push({ field: "Bond Amount", oldValue: data?.bond_amount ?? "", newValue: dataCorrectionForm.bondAmount ?? "" });
      }
      if (Number(dataCorrectionForm.premium || 0) !== Number(data?.premium || 0)) {
        changes.push({ field: "Premium", oldValue: data?.premium ?? "", newValue: dataCorrectionForm.premium ?? "" });
      }
      // Trustee fields
      if ((dataCorrectionForm.legalName || "") !== (data?.trustee_bond?.legal_name || "")) {
        changes.push({ field: "Trust Legal Name", oldValue: data?.trustee_bond?.legal_name || "", newValue: dataCorrectionForm.legalName || "" });
      }
      if ((dataCorrectionForm.trustName || "") !== (data?.trustee_bond?.trust_name || "")) {
        changes.push({ field: "Trust Name", oldValue: data?.trustee_bond?.trust_name || "", newValue: dataCorrectionForm.trustName || "" });
      }
      if ((dataCorrectionForm.aliases || "") !== (data?.trustee_bond?.alias_trustee_legal_name || "")) {
        changes.push({ field: "Trustee Aliases", oldValue: data?.trustee_bond?.alias_trustee_legal_name || "", newValue: dataCorrectionForm.aliases || "" });
      }
      // Trustees list (names only)
      const oldTrustees = (data?.trustees || []).map((t) => t?.name).filter(Boolean).join(", ");
      const newTrustees = (dataCorrectionForm?.trustees || []).map((t) => t?.name).filter(Boolean).join(", ");
      if (oldTrustees !== newTrustees) {
        changes.push({ field: "Trustees", oldValue: oldTrustees, newValue: newTrustees });
      }

      // If correction is county or trustee name and bond is older than 48h, use endorse-bond instead of PATCH
      const ENDORSABLE_FIELDS = ["County", "Trustees"];
      const bondCreatedAt = data?.created_at || null;
      const isOver48h = bondCreatedAt && (Date.now() - new Date(bondCreatedAt).getTime() > 48 * 60 * 60 * 1000);
      const hasEndorsableChange = changes.some((c) => ENDORSABLE_FIELDS.includes(c.field));
      const useEndorsePath = hasEndorsableChange && isOver48h;

      const contactName = [userdetails?.first_name, userdetails?.last_name].filter(Boolean).join(" ") || "Portal User";
      const contactEmail = userdetails?.email || userdetails?.username || "noreply@vermost.com";

      if (useEndorsePath) {
        const toTitleCase = (str) => (str || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
        for (const change of changes) {
          if (change.field === "County") {
            const formData = new FormData();
            formData.append("endorsement_type", "Name_Change_County");
            formData.append("updated_name", toTitleCase(change.newValue));
            formData.append("contact_name", contactName);
            formData.append("contact_email", contactEmail);
            await apiClient(`/endorse-bond/`, "POST", formData, { bond_id }, { "Content-Type": "multipart/form-data" });
          } else if (change.field === "Trustees") {
            const formData = new FormData();
            formData.append("endorsement_type", "Name_Change_Principal");
            formData.append("updated_name", (change.newValue ?? "").toString().trim());
            formData.append("contact_name", contactName);
            formData.append("contact_email", contactEmail);
            await apiClient(`/endorse-bond/`, "POST", formData, { bond_id }, { "Content-Type": "multipart/form-data" });
          }
        }
      } else {
        await apiClient(`/update_trustee_bond/${bond_id}/`, "PATCH", payload);
      }

      const response = await apiClient(`/retrieve-trustee-bond/${bond_id}/`);
      setData(response);
      setBillingMethod(response.trustee_bond.billing_method);
      setIsIndemnityReceived(
        response.trustee_bond?.is_indemnity_received === null
          ? ""
          : response.trustee_bond?.is_indemnity_received
          ? "Yes"
          : "No"
      );
      // Fetch bond updates history
      try {
        const updates = await apiClient(`/bond-updates/${bond_id}/`, "GET");
        setBondUpdates(Array.isArray(updates) ? updates : []);
      } catch (e) {
        console.error("Failed to fetch bond updates:", e);
        setBondUpdates([]);
      }

      const obligeeIdFromApi = getObligeeIdFromResponse(response);
      setGlobalState((prevState) => ({
        ...prevState,
        basicInformation: {
          ...prevState.basicInformation,
          county: response.county,
          courtCaseNumber: response.court_case_number,
          bondAmount: response.bond_amount,
          premiumAmount: response.premium,
          legalName: response.trustee_bond?.legal_name || "",
          trustName: response.trustee_bond?.trust_name || "",
          aliases: response.trustee_bond?.alias_trustee_legal_name || "",
        },
        guardianDetails: {
          ...prevState.guardianDetails,
          guardians: response.trustees,
        },
        documentUpload: {
          ...prevState.documentUpload,
          note: response.trustee_bond?.notes || "",
        },
        obligeeInformation: obligeeIdFromApi !== undefined ? { obligeeId: obligeeIdFromApi } : prevState.obligeeInformation,
      }));

      let role_name = ""
      if (role === "super_user") {
        role_name = "System Admin";
      } 
      else if (role === "internal_user") {
        role_name = "Internal User";
      }
      else if (role === "law_firm_admin") {
        role_name = "Law Firm Admin";
      }
      else if (role === "law_firm_user") {
        role_name = "Law Firm User";
      }  

      // Log bond updates with only changed fields
      try {
        if (changes.length > 0) {
          const updatesPayload = changes.map((change) => ({
            requester_id: userdetails?.id,
            requester_name: `${userdetails?.first_name} ${userdetails?.last_name} (${role_name})`,
            bond_id: bond_id,
            bond_type: "Trustee",
            field_name: change.field,
            old_value: change.oldValue,
            new_value: change.newValue,
          }));
          await apiClient(`/bond-updates/`, "POST", updatesPayload, {}, { "Content-Type": "application/json" });
        }
      } catch (e) {
        console.error("Bond updates logging error:", e);
      }

      // Build styled HTML table for email from changes
      const changesTableHtml = changes.length > 0 ? `
<table style="width:100%; border-collapse:collapse; margin-top:12px; font-family:Arial, sans-serif; font-size:14px;">
  <thead>
    <tr>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Field</th>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Old Value</th>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">New Value</th>
    </tr>
  </thead>
  <tbody>
    ${changes.map(change => `
      <tr>
        <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">${change.field}</td>
        <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#6b7280;">${(change.oldValue ?? "").toString()}</td>
        <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(change.newValue ?? "").toString()}</td>
      </tr>
    `).join('')}
  </tbody>
</table>` : '<p style="font-family:Arial, sans-serif; font-size:14px; color:#6b7280;">No field-level changes detected.</p>';

      // Create bond data correction record and notify (only when using PATCH, not endorse-bond path)
      if (!useEndorsePath) {
      const toSnakeCase = (s) => (s || "").toString().toLowerCase().replace(/\s+/g, "_");
      try {
        await apiClient(
          `/bond-data-corrections/`,
          "POST",
          {
            bond_id: Number(bond_id),
            bond_type: "Trustee",
            bond_number: (response?.bond_number?.bond_number || response?.bond_number?.bondno_id || "").toString(),
            bond_summary: changes.length > 0 ? "Corrected: " + changes.map((c) => c.field).join(", ") : "Data correction",
            reason: dataCorrectionForm.reason || "",
            changes: changes.map((c) => ({ field: toSnakeCase(c.field), from: String(c.oldValue ?? ""), to: String(c.newValue ?? "") })),
            requester_info: { name: contactName, email: contactEmail, role: role_name },
          },
          {},
          { "Content-Type": "application/json" }
        );
      } catch (e) {
        console.error("Bond data correction record error:", e);
      }
      try {
        await apiClient(
          `/send-portal-email/`,
          "POST",
          {
            subject: "Notification: Data Correction Used for Trustee Bond",
            content_html: `<p>Please review the following item.</p>
<p><strong>Requester:</strong> ${userdetails?.first_name} ${userdetails?.last_name}</p>
<p><strong>Bond #:</strong> ${response?.bond_number?.bond_number || response?.bond_number?.bondno_id || ""}</p>
<p><strong>Trust Legal Name:</strong> ${response?.trustee_bond?.legal_name || ""}</p>
<p><strong>Trust Name:</strong> ${response?.trustee_bond?.trust_name || ""}</p>
<p><strong>Trustees:</strong> ${(response?.trustees || [])
  .map((t) => t?.name)
  .filter(Boolean)
  .join(", ")}</p>
<p><strong>Reason for Change:</strong> ${dataCorrectionForm.reason}</p>
${changesTableHtml}`,
            navigate_path: `vermost-trustee-bond-update/${bond_id}`,
            button_text: "Review in Portal",
          },
          {},
          { "Content-Type": "application/json" }
        );
      } catch (e) {
        console.error("Email notification error:", e);
      }
      }

      setIsDataCorrectionOpen(false);
      toast.success("Data updated successfully");
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error("Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box className="p-6 max-w-4xl mx-auto">
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Trustee Bond Request {data?.is_guest ? "(Guest)" : ""}
        </Typography>
        {data?.underwriting_status &&
          data.underwriting_status?.trim()?.toLowerCase() !== "not required" &&
          (role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  color: ["approved", "overrided"].includes(data.underwriting_status?.trim()?.toLowerCase())
                    ? "success.main"
                    : ["pending", "rejected"].includes(data.underwriting_status?.trim()?.toLowerCase())
                      ? "error.main"
                      : "warning.main",
                  mt: 0.25,
                }}
              >
                {["approved", "overrided"].includes(data.underwriting_status?.trim()?.toLowerCase()) ? (
                  <CheckCircle fontSize="small" />
                ) : (
                  <InfoOutlinedIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                  Underwriting
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <div>
                    <span style={{ fontWeight: 700 }}>Status:</span>{" "}
                    <span style={{ fontWeight: 400, color: "inherit" }}>
                      {data.underwriting_status?.trim()?.toLowerCase() === "overrided"
                        ? (data.overrided_by?.trim() || data.overrided_at)
                            ? `Not Required (Overrided by ${(data.overrided_by || "").trim()} at ${data.overrided_at ? new Date(data.overrided_at).toLocaleString() : ""})`
                            : "Not Required"
                        : (() => { const s = data.underwriting_status?.trim()?.toLowerCase(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; })()}
                    </span>
                  </div>
                  {data.underwriting_status?.trim()?.toLowerCase() === "overrided" &&
                    (data.override_reason || "").trim() && (
                      <div><span style={{ fontWeight: 700 }}>Override reason:</span>{" "}<span style={{ fontWeight: 400 }}>{(data.override_reason || "").trim()}</span></div>
                    )}
                  {data.underwriting_status?.trim()?.toLowerCase() !== "overrided" &&
                    data.underwriting_status?.trim()?.toLowerCase() !== "not required" &&
                    data.underwriting_reason && (
                      <div>Reason for Trigger: {data.underwriting_reason}</div>
                    )}
                </Typography>
              </Box>
            </Box>
          )}

        <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenDataCorrection}
          >
            Data Correction
          </Button>
        </Box>
        {isLoading ? (
          <Box
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(255, 255, 255, 0.7)"
            zIndex={9999}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box className="mb-6">
            <BasicInformationForm
              globalData={globalState}
              data={globalState.basicInformation}
              updateData={(data) => updateGlobalState("basicInformation", data)}
              role={role}
              isDisabled={disabled}
            />
            <GeneralInformationForm
              globalData={globalState}
              data={globalState.generalInformation}
              updateData={(data) =>
                updateGlobalState("generalInformation", data)
              }
              role={role}
              isDisabled={disabled}
            />
            <GuardianForm
              globalData={globalState}
              data={globalState.guardianDetails}
              updateData={(data) => updateGlobalState("guardianDetails", data)}
              role={role}
              isDisabled={disabled}
              pii_access={userdetails?.is_pii_user}
            />
            <AttorneyInformationForm
              globalData={globalState}
              data={globalState.attorneyInformation}
              updateData={(data) =>
                updateGlobalState("attorneyInformation", data)
              }
              role={role}
              isDisabled={disabled}
              isGuest={data?.is_guest}
            />
            <ObligeeInformationForm
              globalData={globalState}
              data={globalState.obligeeInformation}
              updateData={(data) =>
                updateGlobalState("obligeeInformation", data)
              }
              role={role}
              isDisabled={disabled}
              suppressDefaultObligee
            />
            <RequestorInformationForm
              globalData={globalState}
              data={globalState.requestorInformation}
              updateData={(data) =>
                updateGlobalState("requestorInformation", data)
              }
              role={role}
              isDisabled={disabled}
            />
            <DocumentUploadForm
              globalData={globalState}
              data={globalState.documentUpload}
              updateData={(data) => updateGlobalState("documentUpload", data)}
              role={role}
              pii_access = {userdetails?.is_pii_user}
              isDisabled={disabled}
            />
            <BillingInformationForm
              globalData={globalState}
              data={globalState.billingInformation}
              updateData={(data) =>
                updateGlobalState("billingInformation", data)
              }
              role={role}
              isDisabled={disabled}
            />
            {(role === "super_user" || role === "internal_user") && (
              <BondNumberSelect
                data={globalState.bondNumber}
                updateData={(data) => updateGlobalState("bondNumber", data)}
              />
            )}
            {(role === "super_user" || role === "internal_user") && (
              <Box
                sx={{
                  maxWidth: 765,
                  mx: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  padding: 3,
                  mt: 3,
                  mb: 3,
                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                  backgroundColor: "#f3f4f6", // exact match for bg-gray-100
                }}
              >
                <Typography variant="h5" className="font-bold mb-4">
                  Billing Method
                </Typography>

                <RadioGroup
                  row
                  value={billingMethod}
                  onChange={(e) => setBillingMethod(e.target.value)}
                >
                  <FormControlLabel
                    value="Direct Bill"
                    control={<Radio />}
                    label="Direct Bill"
                    sx={{ mr: 4 }}
                  />
                  <FormControlLabel
                    value="Agency Bill"
                    control={<Radio />}
                    label="Agency Bill"
                  />
                </RadioGroup>
              </Box>
            )}
            {(role === "super_user" || role === "internal_user") && (
              <Box
                sx={{
                  maxWidth: 765,
                  mx: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  padding: 3,
                  mt: 3,
                  mb: 3,
                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                  backgroundColor: "#f3f4f6", // exact match for bg-gray-100
                }}
              >
                <Typography variant="h5" className="font-bold mb-4">
                  Additional Information
                </Typography>

                <TextField
                  select
                  label="Is Signed Indemnity Received"
                  value={isIndemnityReceived}
                  onChange={(e) => setIsIndemnityReceived(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">Not Selected</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Created At"
                  type="datetime-local"
                  value={editableCreatedAt}
                  onChange={(e) => setEditableCreatedAt(e.target.value)}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                  helperText={data?.created_at ? `Current: ${new Date(data.created_at).toLocaleString()}` : ""}
                />
              </Box>
            )}
          </Box>
        )}
        {(role === "super_user" || role === "internal_user") && (
          <Box
            className="flex justify-center"
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleFinish}
              disabled={isSubmitLoading}
              endIcon={isSubmitLoading && <CircularProgress size={20} />}
            >
              {isSubmitLoading ? "Updating..." : "Update"}
            </Button>
            {/* --------------- APPROVE BUTTON + MENU --------------- */}
            <Button
              variant="contained"
              color="success"
              onClick={handleButtonClick}
              disabled={isApproveLoading}
              endIcon={
                isApproveLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <ArrowDropDownIcon />
                )
              }
            >
              {isApproveLoading ? "Approving…" : "Approve"}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() =>
                  handleOptionSelect({
                    ams_flow: true, // Invoice flow
                  })
                }
              >
                Approve with Policy
              </MenuItem>

              <MenuItem
                onClick={() =>
                  handleOptionSelect({
                    ams_flow: false, // No Invoice
                  })
                }
              >
                Approve without Policy
              </MenuItem>
            </Menu>

            <Button
              variant="contained"
              color="error"
              onClick={() => setRejectDialogOpen(true)}
              disabled={isRejectLoading}
              endIcon={isRejectLoading && <CircularProgress size={20} />}
            >
              {isRejectLoading ? "Rejecting..." : "Reject"}
            </Button>
            {/* Underwriting Dropdown */}
            <Button
              variant="contained"
              color="warning"
              onClick={handleUnderwritingMenuClick}
              disabled={isUnderwritingLoading}
              endIcon={
                isUnderwritingLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <ArrowDropDownIcon />
                )
              }
            >
              {isUnderwritingLoading ? "Processing..." : "Underwriting"}
            </Button>

            <Menu
              anchorEl={underwritingAnchorEl}
              open={Boolean(underwritingAnchorEl)}
              onClose={handleUnderwritingMenuClose}
            >
              <MenuItem onClick={handleManualUnderwritingClick}>
                Trigger Underwriting
              </MenuItem>
              <MenuItem onClick={handleOverrideUnderwritingClick}>
                Override Underwriting
              </MenuItem>
              <MenuItem onClick={() => setIsCreditDialogOpen(true)}>
                Upload Credit Report
              </MenuItem>
            </Menu>

            <Button
              variant="contained"
              color="info"
              onClick={handleSignIndemnity}
              disabled={isEsignatureLoading || data?.status === "Active" || globalState.basicInformation?.is_esignatures === "No" || data?.third_document_url === null}
              endIcon={isEsignatureLoading && <CircularProgress size={20} />}
            >
              {isEsignatureLoading ? "Signing..." : "Trigger Formstack"}
            </Button>
          </Box>
        )}

        {role === "carrier_team_member" && (
          <Box
            className="flex justify-center"
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={() => handleUpdateUnderwriting("approved")}
              disabled={isApproveLoading || !data?.is_underwriting}
              endIcon={isApproveLoading && <CircularProgress size={20} />}
            >
              {isApproveLoading ? "Approving..." : "Approve"}
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={() => handleUpdateUnderwriting("rejected")}
              disabled={isRejectLoading || !data?.is_underwriting}
              endIcon={isRejectLoading && <CircularProgress size={20} />}
            >
              {isRejectLoading ? "Rejecting..." : "Reject"}
            </Button>
          </Box>
        )}
      </Box>

      {(role === "carrier_team_member" ||
        role === "super_user" ||
        role === "internal_user") && (
        <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1500 }}>
          <Badge
            overlap="circular"
            color="success"
            invisible={!chatUnreadTotal}
            badgeContent={chatUnreadTotal}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ '& .MuiBadge-badge': { top: -6, right: -6, zIndex: 2, transform: 'none', minWidth: 18, height: 18, fontSize: 11, fontWeight: 600, boxShadow: 1 } }}
          >
            <Fab
              aria-label="chat"
              sx={{ background: "linear-gradient(135deg, #111 0%, #333 100%)", color: "#fff", border: "1px solid #444", boxShadow: 6, "&:hover": { background: "linear-gradient(135deg, #1a1a1a 0%, #4d4d4d 100%)" } }}
              onClick={() => setOpenChat(!openChat)}
            >
              {openChat ? (
                <CloseIcon sx={{ color: "#fff" }} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.2,
                  }}
                >
                  <ChatIcon sx={{ color: "#fff" }} />
                  <Typography variant="body1" sx={{ fontSize: 8, color: "#fff" }}>
                    UW
                  </Typography>
                </Box>
              )}
            </Fab>
          </Badge>
        </Box>
      )}
      <Fab
        aria-label="history"
        sx={{ position: "fixed", bottom: 16, right: 84, background: "linear-gradient(135deg, #111 0%, #333 100%)", color: "#fff", border: "1px solid #444", boxShadow: 6, "&:hover": { background: "linear-gradient(135deg, #1a1a1a 0%, #4d4d4d 100%)" } }}
        onClick={toggleHistoryDrawer}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.2,
          }}
        >
          <HistoryIcon sx={{ color: "#fff" }} />
          <Typography variant="body1" sx={{ fontSize: 8, color: "#fff" }}>
            History
          </Typography>
        </Box>
      </Fab>
      {/* Render ChatWindow if openChat is true */}
      {openChat &&
        (role === "carrier_team_member" ||
          role === "super_user" ||
          role === "internal_user") && (
          <ChatWindow
            username={username}
            bondId={bond_id}
            role={role}
            onClose={() => setOpenChat(false)}
          />
        )}

      <Drawer
        anchor="right"
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        PaperProps={{ sx: { width: 560, backgroundColor: "#f8fafc" } }}
      >
        <Box p={3}>
          <Tabs value={historyTab} onChange={(e, v) => setHistoryTab(v)} variant="fullWidth" sx={{ minHeight: 44, '& .MuiTabs-indicator': { height: 3, borderRadius: 1 } }}>
            <Tab label="Endorsements" sx={{ fontSize: "0.95rem" }} />
            <Tab label="Updates" sx={{ fontSize: "0.95rem" }} />
          </Tabs>

          {historyTab === 0 && (
            <Box mt={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: "1.15rem" }}>
                Endorsement History
              </Typography>
              {isHistoryLoading ? (
                <Box display="flex" justifyContent="center" mt={5}>
                  <CircularProgress />
                </Box>
              ) : endorsementLogs.length === 0 ? (
                <Typography>No endorsements found.</Typography>
              ) : (
                endorsementLogs.map((log, index) => (
                  <Box key={index} sx={{ position: "relative", pl: 4, mb: 2 }}>
                    <Box sx={{ position: "absolute", left: 8, top: 12, width: 10, height: 10, borderRadius: "50%", backgroundImage: 'linear-gradient(135deg,#3b82f6 0%,#06b6d4 100%)' }} />
                    <Box sx={{ borderLeft: "2px solid #e5e7eb", ml: 1, pl: 2 }}>
                      <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: "1rem" }}>
                          {log.endorsement_type}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "gray", fontSize: "0.95rem" }}>
                          {new Date(log.endorsed_at).toLocaleString()}
                        </Typography>
                        {(log.contact_name || log.contact_email) && (
                          <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.95rem", mt: 0.5 }}>
                            Contact: {log.contact_name || ""}{log.contact_name && log.contact_email ? " - " : ""}{log.contact_email || ""}
                          </Typography>
                        )}
                        <Typography mt={1} sx={{ fontSize: "0.98rem" }}>{log.endorsement_description}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          )}

          {historyTab === 1 && (
            <Box mt={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: "1.15rem" }}>
                Bond Updates
              </Typography>
              {Array.isArray(bondUpdates) && bondUpdates.length > 0 ? (
                bondUpdates
                  .slice()
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((u) => (
                    <Box key={u.bond_update_id} sx={{ position: "relative", pl: 4, mb: 2 }}>
                      <Box sx={{ position: "absolute", left: 8, top: 6, width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10b981" }} />
                      <Box sx={{ borderLeft: "2px solid #e5e7eb", ml: 1, pl: 2 }}>
                        <Typography variant="body2" sx={{ color: "gray", fontSize: "0.95rem" }}>{new Date(u.created_at).toLocaleString()}</Typography>
                        <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                          <strong>{u.requester_name}</strong> changed <strong>{u.field_name}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "0.98rem" }}>
                          {String(u.old_value ?? "")} to <strong>{String(u.new_value ?? "")}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  ))
              ) : (
                <Typography>No updates found.</Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          if (!rejectFcraUploading) {
            setRejectDialogOpen(false);
            setRejectFcraFile(null);
          }
        }}
      >
        <DialogTitle>Reject Bond</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You may optionally attach an FCRA document before rejecting. You can also reject without uploading.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={rejectFcraUploading}
            startIcon={<CloudUploadIcon />}
            sx={{
              borderRadius: 2,
              borderColor: "rgba(37, 99, 235, 0.5)",
              color: "#2563eb",
              fontWeight: 600,
              textTransform: "uppercase",
              mb: 1,
              "&:hover": {
                borderColor: "#2563eb",
                backgroundColor: "rgba(37, 99, 235, 0.04)",
              },
            }}
          >
            Choose File
            <input
              type="file"
              hidden
              accept="application/pdf,.pdf,image/*"
              onChange={(e) => setRejectFcraFile(e.target.files?.[0] || null)}
              disabled={rejectFcraUploading}
            />
          </Button>
          {rejectFcraFile && (
            <Typography variant="body2" sx={{ display: "block", mt: 1, color: "text.primary" }}>
              Selected File: {rejectFcraFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectFcraFile(null);
            }}
            color="secondary"
            disabled={rejectFcraUploading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleReject()}
            disabled={isRejectLoading}
          >
            Reject without FCRA
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isRejectLoading || rejectFcraUploading}
            onClick={async () => {
              if (rejectFcraFile) {
                setRejectFcraUploading(true);
                try {
                  const link = await uploadToADLS(rejectFcraFile);
                  await handleReject({
                    fcraDocumentFileName: rejectFcraFile.name,
                    fcraDocumentLink: link,
                  });
                } catch (err) {
                  console.error(err);
                  toast.error("Upload failed. Rejecting without FCRA document.");
                  await handleReject();
                } finally {
                  setRejectFcraUploading(false);
                }
              } else {
                await handleReject();
              }
            }}
            endIcon={rejectFcraUploading && <CircularProgress size={20} color="inherit" />}
          >
            {rejectFcraUploading ? "Uploading..." : "Upload & Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCreditDialogOpen}
        onClose={() => setIsCreditDialogOpen(false)}
      >
        <DialogTitle>Upload Credit Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload the credit report PDF to notify the underwriter.
          </Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              padding: "6px 12px",
              borderRadius: "4px",
              backgroundColor: "#fafafa",
              display: "inline-block",
              fontSize: "0.9rem",
              cursor: isCreditUploading ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setCreditReportFile(e.target.files[0])}
              disabled={isCreditUploading}
              style={{ fontSize: "0.85rem", width: "100%" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCreditDialogOpen(false)}
            color="secondary"
            disabled={isCreditUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!creditReportFile) {
                toast.error("Please upload a PDF file.");
                return;
              }
              setIsCreditUploading(true);
              try {
                await runCreditReport(bond_id, creditReportFile);
                toast.success("Credit report uploaded successfully.");
                setIsCreditDialogOpen(false);
                setCreditReportFile(null);
              } catch (error) {
                console.error(error);
                toast.error("Upload failed. Try again.");
              } finally {
                setIsCreditUploading(false);
              }
            }}
            disabled={isCreditUploading}
            endIcon={isCreditUploading && <CircularProgress size={20} />}
          >
            {isCreditUploading ? "Uploading..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isDataCorrectionOpen}
        onClose={() => { setIsDataCorrectionOpen(false); setDataCorrectionTab(0); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Data Correction</DialogTitle>
        <Tabs value={dataCorrectionTab} onChange={(_, v) => setDataCorrectionTab(v)} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          <Tab label="Correct data" />
          <Tab label="Correction history" />
        </Tabs>
        <DialogContent>
          {dataCorrectionTab === 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Autocomplete
              options={counties}
              value={dataCorrectionForm.county || ""}
              onChange={(event, value) =>
                setDataCorrectionForm((prev) => ({ ...prev, county: value }))
              }
              loading={loadingCounties}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="County"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCounties ? (
                          <CircularProgress size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Court Case Number"
              value={dataCorrectionForm.courtCaseNumber}
              onChange={(e) =>
                setDataCorrectionForm((prev) => ({
                  ...prev,
                  courtCaseNumber: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Bond Amount"
              value={dataCorrectionForm.bondAmount}
              onChange={handleBondAmountChange}
              fullWidth
            />
            <TextField
              label="Premium"
              value={dataCorrectionForm.premium}
              disabled
              fullWidth
            />
            
            <Typography variant="h6" sx={{ mt: 2 }}>
              Trustee Information
            </Typography>
            <TextField
              label="Legal Name"
              value={dataCorrectionForm.legalName}
              onChange={(e) =>
                setDataCorrectionForm((prev) => ({
                  ...prev,
                  legalName: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Trust Name"
              value={dataCorrectionForm.trustName}
              onChange={(e) =>
                setDataCorrectionForm((prev) => ({
                  ...prev,
                  trustName: e.target.value,
                }))
              }
              fullWidth
            />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Trustees
            </Typography>
            {dataCorrectionForm.trustees.map((trustee, index) => (
              <Box
                key={index}
                sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
              >
                <TextField
                  label={`Trustee ${index + 1}'s Name`}
                  value={trustee.name || ""}
                  onChange={(e) => {
                    const newTrustees = [...dataCorrectionForm.trustees];
                    newTrustees[index] = {
                      ...newTrustees[index],
                      name: e.target.value,
                    };
                    setDataCorrectionForm((prev) => ({
                      ...prev,
                      trustees: newTrustees,
                    }));
                  }}
                  fullWidth
                />
              </Box>
            ))}

            <Typography variant="h6" sx={{ mt: 2 }}>
              Reason for Change
            </Typography>
            <TextField
              label="Reason for Change"
              value={dataCorrectionForm.reason}
              onChange={(e) =>
                setDataCorrectionForm((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
              fullWidth
              multiline
              rows={2}
              required
              error={!dataCorrectionForm.reason}
              helperText={!dataCorrectionForm.reason ? "Please provide a reason for the change" : ""}
            />
          </Box>
          )}
          {dataCorrectionTab === 1 && (
            <Box sx={{ mt: 2 }}>
              {bondDataCorrectionsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
              ) : bondDataCorrectionsList.length === 0 ? (
                <Typography color="text.secondary">No correction history for this bond.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: "#f0f0f0", color: "#111", fontWeight: 700, borderBottomColor: "rgba(0,0,0,0.12)" } }}>
                        <TableCell>Date</TableCell>
                        <TableCell>Field</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Requester</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bondDataCorrectionsList.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</TableCell>
                          <TableCell>{formatCorrectionFieldLabel(row.changes?.field)}</TableCell>
                          <TableCell>{formatCorrectionFromTo(row.changes?.field, row.changes?.from)}</TableCell>
                          <TableCell>{formatCorrectionFromTo(row.changes?.field, row.changes?.to)}</TableCell>
                          <TableCell>{row.requester_info?.name ?? "—"} {row.requester_info?.role ? `(${row.requester_info.role})` : ""}</TableCell>
                          <TableCell>{row.status ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsDataCorrectionOpen(false); setDataCorrectionTab(0); }}>Cancel</Button>
          {dataCorrectionTab === 0 && (
          <Button
            variant="contained"
            onClick={handleSubmitDataCorrection}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Manual underwriting creation dialog */}
      <Dialog open={manualUnderwritingDialogOpen} onClose={() => setManualUnderwritingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trigger Underwriting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            placeholder="Enter reason for creating underwriting entry"
            value={manualUnderwritingReason}
            onChange={(e) => setManualUnderwritingReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            error={!(manualUnderwritingReason || "").trim()}
            helperText={!(manualUnderwritingReason || "").trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManualUnderwritingDialogOpen(false); setManualUnderwritingReason(""); }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmitManualUnderwriting} disabled={isUnderwritingLoading || !(manualUnderwritingReason || "").trim()}>
            {isUnderwritingLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override underwriting reason dialog */}
      <Dialog open={overrideReasonDialogOpen} onClose={() => setOverrideReasonDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Underwriting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for override"
            placeholder="Enter reason for overriding underwriting"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            error={!(overrideReason || "").trim()}
            helperText={!(overrideReason || "").trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideReasonDialogOpen(false); setOverrideReason(""); }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSubmitOverrideReason} disabled={isUnderwritingLoading || !(overrideReason || "").trim()}>
            {isUnderwritingLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VermostTrusteeBondRetrieveForm;
